/**
 * HTML to RTE JSON Conversion Module
 *
 * This module converts generated blog HTML content to a ProseMirror-compatible JSON structure
 * that can be loaded into rich text editors (like TipTap, Draft.js, etc.) while preserving
 * all spacing, formatting, and style information from the Template Builder.
 *
 * Architecture inspired by:
 * - TipTap's generateJSON/generateHTML utilities
 * - Draft.js's stateFromHTML/stateToHTML pattern
 *
 * The JSON structure follows ProseMirror's document model:
 * {
 *   type: 'doc',
 *   content: [
 *     { type: 'heading', attrs: { level: 1 }, content: [...] },
 *     { type: 'paragraph', content: [...] },
 *     ...
 *   ]
 * }
 */

import { parseLogger } from '@/lib/logger';
import { isInternalHref } from './linkClassification';
import type { TemplateBlock, BlockType, PreviewSpacing } from './types';

// One scoped host reaches all synchronous link emitters and is restored in `finally`.
let activeSiteHost: string | undefined;

// =============================================================================
// Callout Style Presets (inlined from blockDefinitions.ts)
// =============================================================================

const CALLOUT_CSS_PRESETS: Record<string, Record<string, string>> = {
  "key-takeaways": {
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    border: "1px solid #e2e8f0",
    borderLeft: "4px solid #3b82f6",
    padding: "0.75rem",
    margin: "0",
    borderRadius: "0 12px 12px 0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(59,130,246,0.06)",
  },
  "table-of-contents": {
    background: "transparent",
    borderTop: "1px solid #e2e8f0",
    borderBottom: "1px solid #e2e8f0",
    padding: "0.75rem 0",
    margin: "1rem 0 1.25rem 0",
    borderRadius: "0",
  },
  "quick-picks": {
    background: "#f0fdf4",
    borderLeft: "4px solid #22c55e",
    padding: "0.75rem",
    margin: "0",
    borderRadius: "0 8px 8px 0",
  },
  callout: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderLeft: "4px solid #64748b",
    padding: "0.75rem",
    margin: "0",
    borderRadius: "0 10px 10px 0",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  },
  "pro-tip": {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderLeft: "4px solid #64748b",
    padding: "0.75rem",
    margin: "0",
    borderRadius: "0 10px 10px 0",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  },
  warning: {
    background: "#fef2f2",
    borderLeft: "4px solid #ef4444",
    padding: "0.75rem",
    margin: "0",
    borderRadius: "0 8px 8px 0",
  },
  verdict: {
    background: "#eff6ff",
    border: "2px solid #3b82f6",
    padding: "1rem",
    margin: "0",
    borderRadius: "8px",
  },
  rating: {
    background: "linear-gradient(135deg, #1e293b, #334155)",
    color: "white",
    padding: "1rem",
    margin: "0",
    borderRadius: "12px",
  },
  info: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderLeft: "4px solid #64748b",
    padding: "0.75rem",
    margin: "0",
    borderRadius: "0 10px 10px 0",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  },
  quote: {
    background: "#f3f4f6",
    borderLeft: "4px solid #22c55e",
    padding: "1rem 1.25rem",
    margin: "1.25rem 0",
    borderRadius: "0 12px 12px 0",
    fontStyle: "italic",
    color: "#111827",
  },
  success: {
    background: "#f0fdf4",
    border: "2px solid #22c55e",
    padding: "1rem",
    margin: "0",
    borderRadius: "8px",
  },
  "expert-quote": {
    background: "#f3f4f6",
    borderLeft: "4px solid #22c55e",
    padding: "1rem 1.25rem",
    margin: "1.25rem 0",
    borderRadius: "0 12px 12px 0",
    fontStyle: "italic",
    color: "#111827",
  },
};

const BODY_TEXT_COLOR = "#1e293b";
const HEADING_TEXT_COLOR = "#111827";

/**
 * Convert CSS properties object to inline style string
 */
function cssToInlineStyle(css: Record<string, string>): string {
  return Object.entries(css)
    .map(([key, value]) => {
      // Convert camelCase to kebab-case
      const kebabKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();
      return `${kebabKey}: ${value}`;
    })
    .join("; ");
}

function combineInlineStyles(...styles: Array<string | undefined>): string {
  return styles
    .map(style => style?.trim().replace(/;$/, ''))
    .filter(Boolean)
    .join('; ');
}

function bodyTextStyle(): string {
  return `color: ${BODY_TEXT_COLOR}`;
}

// FAQ block styling. The editor styles these via `.tiptap-blog-editor` CSS,
// which does not exist on WordPress — so published FAQs fell back to whatever
// the client theme does (commonly collapsing them into an accordion with a
// washed-out question heading). Emit the same look inline, matching the
// pattern paragraphs/lists already use to survive page-builder themes.
const FAQ_SCHEMA_STYLE = 'margin: 1.5rem 0';
const FAQ_ITEM_STYLE =
  'border: 1px solid #e5e7eb; border-radius: 0.5rem; margin-bottom: 0.75rem; overflow: hidden';
const FAQ_QUESTION_STYLE =
  `background-color: #f9fafb; padding: 1rem; font-weight: 600; margin: 0; color: ${HEADING_TEXT_COLOR}`;
// display:block defends against themes that collapse the answer (display:none)
// to build an accordion — an inline style outranks their stylesheet rule.
const FAQ_ANSWER_STYLE = 'display: block; padding: 1rem';

function headingTextStyle(): string {
  return `color: ${HEADING_TEXT_COLOR}`;
}

// =============================================================================
// Environment Detection
// =============================================================================

/**
 * Check if we're running in a browser environment with DOM APIs available
 */
function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof DOMParser !== 'undefined';
}

/**
 * Create a simple fallback document for server-side rendering
 * Parses basic HTML structure without full DOM support
 */
function createFallbackDocument(html: string): RTEDocument {
  const content: RTENode[] = [];

  // Simple regex-based parsing for common elements (server-side fallback)
  const patterns: Array<{ regex: RegExp; handler: (match: RegExpMatchArray) => RTENode | null }> = [
    // Headings
    { regex: /<h([1-6])[^>]*>(.*?)<\/h\1>/gi, handler: (m) => ({ type: 'heading', attrs: { level: parseInt(m[1]) }, content: [{ type: 'text', text: stripTags(m[2]) }] }) },
    // Paragraphs
    { regex: /<p[^>]*>(.*?)<\/p>/gi, handler: (m) => ({ type: 'paragraph', content: [{ type: 'text', text: stripTags(m[1]) }] }) },
    // Lists (simplified - just get items)
    { regex: /<ul[^>]*>(.*?)<\/ul>/gis, handler: (m) => ({ type: 'bulletList', content: extractListItems(m[1]) }) },
    { regex: /<ol[^>]*>(.*?)<\/ol>/gis, handler: (m) => ({ type: 'orderedList', content: extractListItems(m[1]) }) },
    // Blockquotes
    { regex: /<blockquote[^>]*>(.*?)<\/blockquote>/gi, handler: (m) => ({ type: 'blockquote', content: [{ type: 'paragraph', content: [{ type: 'text', text: stripTags(m[1]) }] }] }) },
    // Images
    { regex: /<img[^>]*src=["']([^"']+)["'][^>]*alt=["']([^"']*)["'][^>]*\/?>/gi, handler: (m) => ({ type: 'image', attrs: { src: m[1], alt: m[2] } }) },
    { regex: /<img[^>]*src=["']([^"']+)["'][^>]*\/?>/gi, handler: (m) => ({ type: 'image', attrs: { src: m[1], alt: '' } }) },
    // Horizontal rules
    { regex: /<hr[^>]*\/?>/gi, handler: () => ({ type: 'horizontalRule' }) },
  ];

  const remaining = html;
  for (const { regex, handler } of patterns) {
    let match;
    regex.lastIndex = 0;
    while ((match = regex.exec(remaining)) !== null) {
      const node = handler(match);
      if (node) content.push(node);
    }
  }

  return { type: 'doc', content: content.length > 0 ? content : undefined } as RTEDocument;
}

/**
 * Strip HTML tags from text (for server-side fallback)
 */
function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}

/**
 * Extract list items from HTML (for server-side fallback)
 */
function extractListItems(html: string): RTENode[] {
  const items: RTENode[] = [];
  const liRegex = /<li[^>]*>(.*?)<\/li>/gi;
  let match;
  while ((match = liRegex.exec(html)) !== null) {
    items.push({
      type: 'listItem',
      content: [{ type: 'paragraph', content: [{ type: 'text', text: stripTags(match[1]) }] }],
    });
  }
  return items;
}

// =============================================================================
// RTE Node Types - ProseMirror-compatible document structure
// =============================================================================

export interface RTEMark {
  type: 'bold' | 'italic' | 'underline' | 'strike' | 'code' | 'link' | 'textStyle';
  attrs?: Record<string, unknown>;
}

export interface RTENode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: RTENode[];
  marks?: RTEMark[];
  text?: string;
}

export interface RTEDocument {
  type: 'doc';
  content?: RTENode[];
  // Custom metadata for Template Builder integration
  meta?: {
    spacing?: PreviewSpacing;
    gridColumnCount?: number;
    version?: string;
  };
}

const PARSER_TRACE_MARK_TYPES = new Set(['link', 'bold', 'italic', 'underline', 'strike', 'textStyle']);

function traceSnippet(value: string | undefined, limit = 80): string {
  const text = value || '';
  return text
    .slice(0, limit)
    .replace(/ /g, '[space]')
    .replace(/\n/g, '\\n')
    .replace(/\t/g, '\\t');
}

function getMarkTypes(node: RTENode): string[] {
  return (node.marks || [])
    .map((mark) => mark.type)
    .filter((type) => PARSER_TRACE_MARK_TYPES.has(type));
}

function collectTextNodes(node: RTENode, output: RTENode[] = []): RTENode[] {
  if (node.type === 'text') {
    output.push(node);
  }
  node.content?.forEach((child) => collectTextNodes(child, output));
  return output;
}

export function traceRTEDocumentWhitespace(
  stage: string,
  doc: RTEDocument | undefined | null,
  context: Record<string, unknown> = {},
): void {
  if (!doc?.content?.length) return;

  const samples: Array<Record<string, unknown>> = [];
  for (const [blockIndex, block] of doc.content.entries()) {
    const textNodes = collectTextNodes(block);
    textNodes.forEach((node, nodeIndex) => {
      const markTypes = getMarkTypes(node);
      if (markTypes.length === 0) return;

      const previous = textNodes[nodeIndex - 1]?.text || '';
      const current = node.text || '';
      const next = textNodes[nodeIndex + 1]?.text || '';
      samples.push({
        block_index: blockIndex,
        block_type: block.type,
        node_index: nodeIndex,
        mark_types: markTypes,
        previous_tail: traceSnippet(previous.slice(-40), 40),
        current: traceSnippet(current, 80),
        next_head: traceSnippet(next.slice(0, 40), 40),
        previous_has_trailing_space: previous ? /\s$/.test(previous) : null,
        current_has_leading_space: current ? /^\s/.test(current) : null,
        current_has_trailing_space: current ? /\s$/.test(current) : null,
        next_has_leading_space: next ? /^\s/.test(next) : null,
      });
    });
    if (samples.length >= 8) break;
  }

  parseLogger.debug('[PARSER_TRACE] rte_document_whitespace', {
    stage,
    ...context,
    top_level_nodes: doc.content.length,
    marked_samples: samples.slice(0, 8),
  });
}

function traceHTMLInlineBoundaries(
  stage: string,
  html: string,
  context: Record<string, unknown> = {},
): void {
  const matches = Array.from(
    html.matchAll(/(.{0,40})(<(a|strong|b|em|i)\b[\s\S]*?<\/\3>)(.{0,40})/gi),
  ).slice(0, 6);

  parseLogger.debug('[PARSER_TRACE] html_inline_boundaries', {
    stage,
    ...context,
    html_length: html.length,
    samples: matches.map((match) => ({
      before: traceSnippet(match[1], 40),
      inline_html: traceSnippet(match[2], 120),
      after: traceSnippet(match[4], 40),
    })),
  });
}

function traceInlineMarkParse(tagName: string, content: RTENode[], mark: RTEMark): void {
  parseLogger.debug('[PARSER_TRACE] html_inline_mark_parse', {
    tag: tagName,
    mark_type: mark.type,
    href: mark.attrs?.href,
    content_text: traceSnippet(content.map((node) => extractPlainTextFromNode(node)).join(''), 100),
    text_node_count: content.length,
  });
}

// =============================================================================
// RTE Document Filtering Utilities
// =============================================================================

/**
 * Filter out rawHtml nodes from an RTEDocument.
 *
 * This is used when loading content into the TipTap editor, which doesn't have
 * a rawHtml extension and would throw "Invalid content" errors.
 *
 * Note: This filter should ONLY be applied when loading into the editor.
 * When saving, we want to preserve rawHtml nodes in the database.
 *
 * @param content - The RTEDocument to filter
 * @returns A new RTEDocument with rawHtml nodes removed, or null if input is null/undefined
 */
export function filterRawHtmlNodes(content: RTEDocument | null | undefined): RTEDocument | null {
  if (!content?.content) return content ?? null;

  return {
    ...content,
    content: content.content.filter(node => node.type !== 'rawHtml'),
  };
}

/**
 * Count words in an RTEDocument by extracting all text content.
 * This excludes rawHtml nodes (like CTA) from the count.
 *
 * @param content - The RTEDocument to count words from
 * @returns Word count, or 0 if content is null/undefined
 */
export function getWordCountFromRTE(content: RTEDocument | null | undefined): number {
  if (!content?.content) return 0;

  // Recursively extract text from nodes
  function extractText(node: RTENode): string {
    // Skip rawHtml nodes (CTA injection)
    if (node.type === 'rawHtml') return '';

    // Text nodes have direct text content
    if (node.type === 'text' && node.text) {
      return node.text;
    }

    // Recurse into child content
    if (node.content && Array.isArray(node.content)) {
      return node.content.map(extractText).join(' ');
    }

    return '';
  }

  const allText = content.content.map(extractText).join(' ');
  // Count words (split by whitespace, filter empty strings)
  const words = allText.split(/\s+/).filter(word => word.length > 0);
  return words.length;
}

// =============================================================================
// Style Conversion Helpers
// =============================================================================

/**
 * Map of TipTap attribute names to CSS property names.
 * TipTap uses camelCase attrs (textAlign), CSS uses kebab-case (text-align).
 */
const ATTR_TO_CSS_MAP: Record<string, string> = {
  textAlign: 'text-align',
  fontSize: 'font-size',
  fontWeight: 'font-weight',
  fontStyle: 'font-style',
  fontFamily: 'font-family',
  lineHeight: 'line-height',
  color: 'color',
  backgroundColor: 'background-color',
  textDecoration: 'text-decoration',
  marginTop: 'margin-top',
  marginBottom: 'margin-bottom',
  paddingLeft: 'padding-left',
  paddingRight: 'padding-right',
};

/**
 * Build an inline CSS style string from TipTap node attributes.
 * Converts attrs like { textAlign: 'center', fontSize: '1.5rem' }
 * to a CSS string like "text-align: center; font-size: 1.5rem"
 *
 * @param attrs - TipTap node attributes
 * @returns CSS style string, or empty string if no styles
 */
function buildInlineStyleFromAttrs(attrs: Record<string, unknown> | undefined): string {
  if (!attrs) return '';

  const styleParts: string[] = [];

  // First, check if there's already a pre-built style string
  if (attrs.style && typeof attrs.style === 'string') {
    styleParts.push(attrs.style);
  }

  // Then, convert individual TipTap attributes to CSS
  for (const [attrName, cssProperty] of Object.entries(ATTR_TO_CSS_MAP)) {
    const value = normalizeTextColor(attrs[attrName], cssProperty);
    if (value !== undefined && value !== null && value !== '') {
      styleParts.push(`${cssProperty}: ${value}`);
    }
  }

  const result = styleParts.join('; ');

  return result;
}

// =============================================================================
// Node Type Definitions - Mapping between HTML and RTE nodes
// =============================================================================

interface NodeDefinition {
  // HTML tag(s) that map to this node type
  parseHTML: Array<{ tag: string; getAttrs?: (element: HTMLElement) => Record<string, unknown> | false }>;
  // Generate HTML from this node
  renderHTML: (node: RTENode, context?: RenderHTMLContext) => string;
  // Node group (block, inline, etc.)
  group: 'block' | 'inline' | 'custom';
  // What content this node can contain
  content?: string;
}

interface RenderHTMLContext {
  insideTableOfContents?: boolean;
}

/**
 * Local, additive extension of RenderHTMLContext for list-item paragraph handling.
 * These internal markers are set/read ONLY inside the listItem + paragraph
 * renderHTML cases below; no other node reads them, so they are kept off the
 * shared RenderHTMLContext interface and cast in where needed.
 *
 * Mirrors the html-service fix (7108a7c73) so the frontend publish-HTML path has
 * renderer parity: on themes with `list-style-position: inside` (e.g. Divi), a
 * block `<p>` as a list item's only child can't share a line with the marker, so
 * ordered-list numbers ("1.", "2.") detach onto their own line; the forced
 * `margin: 0 0 1em 0` also produced oversized inter-item gaps on every theme.
 *
 * - `insideListItem`: paragraph is a direct child of a <li>.
 * - `unwrapListItemParagraph`: the <li> holds exactly one paragraph (the
 *   dominant TipTap shape `listItem > paragraph`); render its inline content
 *   with NO block <p> wrapper so text shares the list marker's line.
 * - `listItemLastChild`: this paragraph is the last block child of a
 *   multi-block <li>, so it drops its bottom margin.
 */
type ListItemRenderContext = RenderHTMLContext & {
  insideListItem?: boolean;
  unwrapListItemParagraph?: boolean;
  listItemLastChild?: boolean;
};

function headingAttrsFromElement(element: HTMLElement, level: number): Record<string, unknown> {
  const attrs: Record<string, unknown> = { level };
  const id = element.getAttribute('id');
  const tocId = element.getAttribute('data-toc-id');
  if (id) attrs.id = id;
  if (tocId) attrs['data-toc-id'] = tocId;
  return attrs;
}

const NODE_DEFINITIONS: Record<string, NodeDefinition> = {
  // Document root - NOTE: 'article' is handled specially in parseElement to unwrap children
  doc: {
    parseHTML: [{ tag: 'body' }],
    renderHTML: (node, context) => node.content?.map(n => renderNodeToHTML(n, context)).join('\n') || '',
    group: 'block',
    content: 'block+',
  },

  // Headings
  heading: {
    parseHTML: [
      { tag: 'h1', getAttrs: (element) => headingAttrsFromElement(element, 1) },
      { tag: 'h2', getAttrs: (element) => headingAttrsFromElement(element, 2) },
      { tag: 'h3', getAttrs: (element) => headingAttrsFromElement(element, 3) },
      { tag: 'h4', getAttrs: (element) => headingAttrsFromElement(element, 4) },
      { tag: 'h5', getAttrs: (element) => headingAttrsFromElement(element, 5) },
      { tag: 'h6', getAttrs: (element) => headingAttrsFromElement(element, 6) },
    ],
    renderHTML: (node, context) => {
      const level = (node.attrs?.level as number) || 1;
      const isTocHeading = context?.insideTableOfContents && (level === 2 || level === 3);
      const blockId = node.attrs?.blockId ? ` data-block-id="${node.attrs.blockId}"` : '';
      const headingId = node.attrs?.id;
      const headingTocId = node.attrs?.['data-toc-id'];
      const id = typeof headingId === 'string' && headingId.trim()
        ? ` id="${escapeAttr(headingId)}"`
        : '';
      const tocId = typeof headingTocId === 'string' && headingTocId.trim()
        ? ` data-toc-id="${escapeAttr(headingTocId)}"`
        : '';
      // Build inline style from TipTap attrs (textAlign, fontSize, etc.)
      const inlineStyle = isTocHeading
        ? combineInlineStyles(
            'font-size: 1rem; line-height: 1.35; margin: 0 0 0.5rem 0; font-weight: 600',
            headingTextStyle()
          )
        : combineInlineStyles(
            headingTextStyle(),
            buildInlineStyleFromAttrs(node.attrs as Record<string, unknown>)
          );
      const style = inlineStyle ? ` style="${inlineStyle}"` : '';
      const className = node.attrs?.class ? ` class="${node.attrs.class}"` : '';
      const content = node.content?.map(n => renderNodeToHTML(n, context)).join('') || '';
      return `<h${level}${blockId}${id}${tocId}${className}${style}>${content}</h${level}>`;
    },
    group: 'block',
    content: 'inline*',
  },

  // Paragraph
  paragraph: {
    parseHTML: [{ tag: 'p' }],
    renderHTML: (node, context) => {
      const listCtx = context as ListItemRenderContext | undefined;
      // Single-paragraph list item: emit the paragraph's inline content with NO block <p>
      // wrapper so the text sits on the list marker's line. Fixes detached ordered-list
      // numbers on `list-style-position: inside` themes (e.g. Divi) and the oversized 1em
      // inter-item gap. Multi-block list items fall through and keep their <p> wrappers.
      if (listCtx?.unwrapListItemParagraph) {
        return node.content?.map(n => renderNodeToHTML(n, context)).join('') || '';
      }
      const blockId = node.attrs?.blockId ? ` data-block-id="${node.attrs.blockId}"` : '';
      // Build inline style from TipTap attrs (textAlign, fontSize, etc.)
      const baseStyle = context?.insideTableOfContents
        ? 'margin: 0'
        : buildInlineStyleFromAttrs(node.attrs as Record<string, unknown>);
      const insideListItem = Boolean(listCtx?.insideListItem);
      // Force visible inter-paragraph spacing — many WP/page-builder themes reset <p> margins to 0.
      // Inside a multi-block <li>, use a tighter margin (0.5em; 0 for the last child) so nested
      // structure stays compact instead of the 1em body-paragraph gap.
      const paragraphStyle = context?.insideTableOfContents
        ? ''
        : insideListItem
        ? (listCtx?.listItemLastChild ? 'margin: 0' : 'margin: 0 0 0.5em 0')
        : 'margin: 0 0 1em 0';
      const combinedStyle = context?.insideTableOfContents
        ? combineInlineStyles(baseStyle, bodyTextStyle())
        : combineInlineStyles(paragraphStyle, bodyTextStyle(), baseStyle);
      const style = ` style="${combinedStyle}"`;
      const className = node.attrs?.class ? ` class="${node.attrs.class}"` : '';
      const content = node.content?.map(n => renderNodeToHTML(n, context)).join('') || '';
      return `<p${blockId}${className}${style}>${content}</p>`;
    },
    group: 'block',
    content: 'inline*',
  },

  // Text node
  text: {
    parseHTML: [],
    renderHTML: (node, context) => {
      let text = escapeHTML(node.text || '');
      // Apply marks - but handle link + textStyle specially
      if (node.marks) {
        const linkMark = node.marks.find((m) => m.type === 'link');
        const textStyleMark = node.marks.find((m) => m.type === 'textStyle');
        const otherMarks = node.marks.filter((m) => m.type !== 'link' && m.type !== 'textStyle');
        const isTocLink = Boolean(context?.insideTableOfContents);

        // Apply other marks first (bold, italic, etc.)
        for (const mark of otherMarks) {
          text = applyMark(text, mark);
        }

        // If we have both link and textStyle, apply style directly to <a> tag
        if (linkMark && textStyleMark) {
          const href = linkMark.attrs?.href || '#';
          // Internal ⇒ strip any stamped target/rel; external ⇒ FORCE
          // target="_blank" + rel (never merely preserve incoming mark attrs).
          const isExternal = !isInternalHref(href, activeSiteHost);
          const target = isExternal ? ' target="_blank"' : '';
          const rel = isExternal ? ' rel="nofollow noopener noreferrer"' : '';
          // For links: exclude color so they remain visually distinct (blue/underlined)
          // Keep other styles like fontSize that don't affect link visibility
          const linkStyleCSS = isTocLink
            ? 'color: #0f172a; text-decoration: none; font-weight: 500'
            : buildTextStyleCSSForLink(textStyleMark.attrs);
          const style = linkStyleCSS ? ` style="${escapeAttr(linkStyleCSS)}"` : '';
          text = `<a href="${escapeAttr(href as string)}"${target}${rel}${style}>${text}</a>`;
        } else {
          // Apply textStyle if present (no link)
          if (textStyleMark) {
            text = applyMark(text, textStyleMark);
          }
          // Apply link if present (no textStyle conflict)
          if (linkMark) {
            if (isTocLink) {
              const href = linkMark.attrs?.href || '#';
              // Internal ⇒ strip any stamped target/rel; external ⇒ FORCE
              // target="_blank" + rel (never merely preserve incoming mark attrs).
              const isExternal = !isInternalHref(href, activeSiteHost);
              const target = isExternal ? ' target="_blank"' : '';
              const rel = isExternal ? ' rel="nofollow noopener noreferrer"' : '';
              const linkStyle = 'color: #0f172a; text-decoration: none; font-weight: 500';
              text = `<a href="${escapeAttr(href as string)}"${target}${rel} style="${escapeAttr(linkStyle)}">${text}</a>`;
            } else {
              text = applyMark(text, linkMark);
            }
          }
        }
      }
      return text;
    },
    group: 'inline',
  },

  // Hard break
  hardBreak: {
    parseHTML: [{ tag: 'br' }],
    renderHTML: () => '<br>',
    group: 'inline',
  },

  // Bullet list - with tighter vertical spacing for WordPress
  bulletList: {
    parseHTML: [{ tag: 'ul' }],
    renderHTML: (node, context) => {
      if (context?.insideTableOfContents) {
        const classValue = typeof node.attrs?.class === 'string'
          ? node.attrs.class
          : 'table-of-contents-list';
        const className = ` class="${escapeAttr(classValue)}"`;
        const attrStyle = typeof node.attrs?.style === 'string' ? node.attrs.style : '';
        const defaultStyle = classValue.includes('table-of-contents-sublist')
          ? 'list-style: none; margin: 0.35rem 0 0.15rem 0.75rem; padding-left: 0.75rem; border-left: 1px solid #e2e8f0'
          : 'list-style: none; margin: 0; padding-left: 0.75rem';
        const style = combineInlineStyles(bodyTextStyle(), attrStyle || defaultStyle);
        const content = node.content?.map(n => renderNodeToHTML(n, context)).join('\n') || '';
        return `<ul${className} style="${escapeAttr(style)}">\n${content}\n</ul>`;
      }
      const blockId = node.attrs?.blockId ? ` data-block-id="${node.attrs.blockId}"` : '';
      const baseStyle = buildInlineStyleFromAttrs(node.attrs as Record<string, unknown>);
      // Add compact list spacing for WordPress publishing
      const listStyle = 'margin: 0.5rem 0; padding-left: 1.5rem';
      const combinedStyle = combineInlineStyles(bodyTextStyle(), listStyle, baseStyle);
      const style = ` style="${combinedStyle}"`;
      const className = node.attrs?.class ? ` class="${node.attrs.class}"` : '';
      const content = node.content?.map(n => renderNodeToHTML(n, context)).join('\n') || '';
      return `<ul${blockId}${className}${style}>\n${content}\n</ul>`;
    },
    group: 'block',
    content: 'listItem+',
  },

  // Ordered list - with tighter vertical spacing for WordPress
  orderedList: {
    parseHTML: [{ tag: 'ol' }],
    renderHTML: (node, context) => {
      const blockId = node.attrs?.blockId ? ` data-block-id="${node.attrs.blockId}"` : '';
      const start = (node.attrs?.start && node.attrs.start !== 1) ? ` start="${node.attrs.start}"` : '';
      const type = node.attrs?.type ? ` type="${node.attrs.type}"` : '';
      const baseStyle = buildInlineStyleFromAttrs(node.attrs as Record<string, unknown>);
      // Add compact list spacing for WordPress publishing
      const listStyle = 'margin: 0.5rem 0; padding-left: 1.5rem';
      const combinedStyle = combineInlineStyles(bodyTextStyle(), listStyle, baseStyle);
      const style = ` style="${combinedStyle}"`;
      const className = node.attrs?.class ? ` class="${node.attrs.class}"` : '';
      const content = node.content?.map(n => renderNodeToHTML(n, context)).join('\n') || '';
      return `<ol${blockId}${start}${type}${className}${style}>\n${content}\n</ol>`;
    },
    group: 'block',
    content: 'listItem+',
  },

  // List item - with tighter vertical spacing for WordPress
  listItem: {
    parseHTML: [{ tag: 'li' }],
    renderHTML: (node, context) => {
      if (context?.insideTableOfContents) {
        // ToC list items keep their existing wrapped <p style="margin: 0"> shape.
        const content = node.content?.map(n => renderNodeToHTML(n, context)).join('') || '';
        const className = typeof node.attrs?.class === 'string'
          ? ` class="${escapeAttr(node.attrs.class)}"`
          : '';
        const attrStyle = typeof node.attrs?.style === 'string' ? node.attrs.style : '';
        const style = combineInlineStyles(bodyTextStyle(), attrStyle || 'margin: 0.35rem 0; line-height: 1.4');
        return `<li${className} style="${escapeAttr(style)}">${content}</li>`;
      }
      // The dominant TipTap shape is `listItem > paragraph`. When a <li> holds exactly one
      // paragraph, render it inline (no <p>) so marker + text share a line. Multi-block items
      // (2+ paragraphs, or paragraph + nested list) keep <p> wrappers but with tighter margins.
      const liChildNodes = node.content || [];
      const isSingleParagraphLi = liChildNodes.length === 1 && liChildNodes[0]?.type === 'paragraph';
      const lastChildIndex = liChildNodes.length - 1;
      const liChildContext: ListItemRenderContext = {
        ...(context || {}),
        insideListItem: true,
        unwrapListItemParagraph: isSingleParagraphLi,
      };
      const content = liChildNodes
        .map((child, index) => {
          // Last paragraph child of a multi-block <li> drops its bottom margin.
          const childContext: ListItemRenderContext =
            index === lastChildIndex && child?.type === 'paragraph'
              ? { ...liChildContext, listItemLastChild: true }
              : liChildContext;
          return renderNodeToHTML(child, childContext);
        })
        .join('') || '';
      // Compact spacing: minimal margin-bottom, normal line-height
      return `<li style="${combineInlineStyles(bodyTextStyle(), 'margin-bottom: 0.125rem; line-height: 1.5')}">${content}</li>`;
    },
    group: 'block',
    content: 'block+',
  },

  // Blockquote
  blockquote: {
    parseHTML: [{ tag: 'blockquote' }],
    renderHTML: (node) => {
      const blockId = node.attrs?.blockId ? ` data-block-id="${node.attrs.blockId}"` : '';
      const inlineStyle = buildInlineStyleFromAttrs(node.attrs as Record<string, unknown>);
      const combinedStyle = combineInlineStyles(bodyTextStyle(), inlineStyle);
      const style = combinedStyle ? ` style="${combinedStyle}"` : '';
      const className = node.attrs?.class ? ` class="${node.attrs.class}"` : '';
      const content = node.content?.map(n => renderNodeToHTML(n)).join('\n') || '';
      const citation = node.attrs?.attribution
        ? `\n<cite>— ${escapeHTML(node.attrs.attribution as string)}</cite>`
        : '';
      return `<blockquote${blockId}${className}${style}>\n${content}${citation}\n</blockquote>`;
    },
    group: 'block',
    content: 'block+',
  },

  // Image
  image: {
    parseHTML: [{
      tag: 'img[src]',
      getAttrs: (el) => ({
        src: el.getAttribute('src'),
        alt: el.getAttribute('alt'),
        title: el.getAttribute('title'),
        width: el.getAttribute('width'),
        height: el.getAttribute('height'),
      }),
    }],
    renderHTML: (node) => {
      const src = typeof node.attrs?.src === 'string' ? node.attrs.src.trim() : '';
      if (!src) return '';

      const attrs: string[] = [];
      attrs.push(`src="${escapeAttr(src)}"`);
      if (node.attrs?.alt) attrs.push(`alt="${escapeAttr(node.attrs.alt as string)}"`);
      if (node.attrs?.title) attrs.push(`title="${escapeAttr(node.attrs.title as string)}"`);
      if (node.attrs?.width) attrs.push(`width="${node.attrs.width}"`);
      if (node.attrs?.height) attrs.push(`height="${node.attrs.height}"`);

      const captionHtml = renderImageCaptionHTML(node.attrs);
      const shouldRenderFigure = Boolean(captionHtml);

      // Build style for alignment and sizing (WordPress/Gutenberg compatible)
      // SEO best practices: max-width responsive, max-height 66vh (2/3 viewport)
      const styleParts: string[] = [
        'max-width: 100%',      // Responsive width
        'height: auto',          // Maintain aspect ratio
        'max-height: 66vh',      // Limit to 2/3 of viewport (SEO/UX best practice)
      ];

      // Default to center alignment for all images when publishing
      const alignment = (node.attrs?.alignment as string) || 'center';
      let isFloated = false;
      if (alignment === 'right') {
        styleParts.push('float: right', 'margin: 0 0 1rem 1.5rem');
        isFloated = true;
      } else if (alignment === 'left') {
        styleParts.push('float: left', 'margin: 0 1.5rem 1rem 0');
        isFloated = true;
      } else {
        // Default: center alignment
        styleParts.push(
          'display: block',
          shouldRenderFigure ? 'margin: 0 auto' : 'margin: 1.5rem auto 1.75rem',
        );
      }

      attrs.push(`style="${styleParts.join('; ')}"`)

      const imgTag = `<img ${attrs.join(' ')} />`;

      if (shouldRenderFigure) {
        const figureStyleParts: string[] = ['max-width: 100%'];
        if (alignment === 'right') {
          figureStyleParts.push('float: right', 'margin: 0 0 1rem 1.5rem');
        } else if (alignment === 'left') {
          figureStyleParts.push('float: left', 'margin: 0 1.5rem 1rem 0');
        } else {
          figureStyleParts.push('margin: 1.5rem auto 1.75rem', 'text-align: center');
        }
        const figcaptionStyle = 'font-size: 0.875rem; color: #6b7280; margin-top: 0.625rem; line-height: 1.4; text-align: center';
        const figcaption = `<figcaption style="${figcaptionStyle}">${captionHtml}</figcaption>`;
        const figure = `<figure style="${figureStyleParts.join('; ')}">\n  ${imgTag}\n  ${figcaption}\n</figure>`;

        if (isFloated) {
          return `${figure}\n<div style="clear: both;"></div>`;
        }
        return figure;
      }

      // Add clearfix after floated images to prevent content overlap
      if (isFloated) {
        return `${imgTag}\n<div style="clear: both;"></div>`;
      }
      return imgTag;
    },
    group: 'block',
  },

  // NOTE: figure/figcaption removed - TipTap doesn't have these extensions
  // Images are parsed directly as 'image' nodes

  // Table (using @tiptap/extension-table)
  table: {
    parseHTML: [{ tag: 'table' }],
    renderHTML: (node) => {
      const blockId = node.attrs?.blockId ? ` data-block-id="${node.attrs.blockId}"` : '';
      const style = node.attrs?.style ? ` style="${node.attrs.style}"` : '';
      const className = node.attrs?.class ? ` class="${node.attrs.class}"` : '';
      const content = node.content?.map(n => renderNodeToHTML(n)).join('\n') || '';
      return `<table${blockId}${className}${style}>\n${content}\n</table>`;
    },
    group: 'block',
    content: 'tableRow+',
  },

  // Table row
  tableRow: {
    parseHTML: [{ tag: 'tr' }],
    renderHTML: (node) => {
      const content = node.content?.map(n => renderNodeToHTML(n)).join('') || '';
      return `<tr>${content}</tr>`;
    },
    group: 'block',
    content: '(tableCell | tableHeader)+',
  },

  // Table header cell
  tableHeader: {
    parseHTML: [{ tag: 'th' }],
    renderHTML: (node) => {
      const style = node.attrs?.style ? ` style="${node.attrs.style}"` : '';
      const colspan = node.attrs?.colspan ? ` colspan="${node.attrs.colspan}"` : '';
      const rowspan = node.attrs?.rowspan ? ` rowspan="${node.attrs.rowspan}"` : '';
      const content = node.content?.map(n => renderNodeToHTML(n)).join('') || '';
      return `<th${colspan}${rowspan}${style}>${content}</th>`;
    },
    group: 'block',
    content: 'block+',
  },

  // Table cell
  tableCell: {
    parseHTML: [{ tag: 'td' }],
    renderHTML: (node) => {
      const style = node.attrs?.style ? ` style="${node.attrs.style}"` : '';
      const colspan = node.attrs?.colspan ? ` colspan="${node.attrs.colspan}"` : '';
      const rowspan = node.attrs?.rowspan ? ` rowspan="${node.attrs.rowspan}"` : '';
      const content = node.content?.map(n => renderNodeToHTML(n)).join('') || '';
      return `<td${colspan}${rowspan}${style}>${content}</td>`;
    },
    group: 'block',
    content: 'block+',
  },

  // Horizontal rule / Divider
  horizontalRule: {
    parseHTML: [{ tag: 'hr' }],
    renderHTML: (node) => {
      const blockId = node.attrs?.blockId ? ` data-block-id="${node.attrs.blockId}"` : '';
      const style = node.attrs?.style ? ` style="${node.attrs.style}"` : '';
      return `<hr${blockId}${style} />`;
    },
    group: 'block',
  },

  // Code block (preformatted code)
  codeBlock: {
    parseHTML: [
      { tag: 'pre', getAttrs: (el) => {
        const code = el.querySelector('code');
        const language = code?.getAttribute('class')?.replace(/^language-/, '') || '';
        return { language };
      }},
    ],
    renderHTML: (node) => {
      const language = node.attrs?.language as string || '';
      const langClass = language ? ` class="language-${language}"` : '';
      const content = node.content?.map(n => renderNodeToHTML(n)).join('') || '';
      return `<pre><code${langClass}>${content}</code></pre>`;
    },
    group: 'block',
    content: 'text*',
  },

  // NOTE: section, aside, details, summary removed - TipTap doesn't have these extensions
  // These elements will be unwrapped during parsing (children parsed directly)

  // =============================================================================
  // Custom Template Builder Blocks - Semantic nodes that preserve rich structure
  // =============================================================================

  // Callout box (key-takeaways, pro-tip, warning, etc.)
  callout: {
    parseHTML: [
      { tag: 'div.key-takeaways', getAttrs: () => ({ variant: 'key-takeaways' }) },
      { tag: 'div.table-of-contents', getAttrs: () => ({ variant: 'table-of-contents' }) },
      { tag: 'div.callout-table-of-contents', getAttrs: () => ({ variant: 'table-of-contents' }) },
      { tag: 'div.pro-tip', getAttrs: () => ({ variant: 'pro-tip' }) },
      { tag: 'div.warning', getAttrs: () => ({ variant: 'warning' }) },
      { tag: 'div.info', getAttrs: () => ({ variant: 'info' }) },
      { tag: 'div.verdict', getAttrs: () => ({ variant: 'verdict' }) },
      { tag: 'div.callout-quote', getAttrs: () => ({ variant: 'quote' }) },
      { tag: 'div.quote', getAttrs: () => ({ variant: 'quote' }) },
      { tag: 'div.callout-expert-quote', getAttrs: () => ({ variant: 'expert-quote' }) },
      { tag: 'div.callout-citation', getAttrs: () => ({ variant: 'citation' }) },
    ],
    renderHTML: (node, context) => {
      const blockId = node.attrs?.blockId ? ` data-block-id="${node.attrs.blockId}"` : '';
      const variant = (node.attrs?.variant as string) || 'callout';
      const title = node.attrs?.title as string;
      const attribution = node.attrs?.attribution as string;
      const style = node.attrs?.stylePreset
        ? cssToInlineStyle(CALLOUT_CSS_PRESETS[node.attrs.stylePreset as string] || {})
        : (node.attrs?.style as string || '');
      const combinedStyle = combineInlineStyles(bodyTextStyle(), style);
      const styleAttr = combinedStyle ? ` style="${combinedStyle}"` : '';
      const isToc = variant === 'table-of-contents' || node.attrs?.stylePreset === 'table-of-contents';
      const childContext = isToc ? { ...(context || {}), insideTableOfContents: true } : context;
      // Skip the first child heading when title is present — its text is
      // already rendered as the bold title prefix below, so leaving it in
      // body would duplicate the title.
      let nodeContent = node.content;
      if (title && Array.isArray(nodeContent)) {
        const firstHeadingIdx = nodeContent.findIndex(n => n.type === 'heading');
        if (firstHeadingIdx >= 0) {
          nodeContent = [
            ...nodeContent.slice(0, firstHeadingIdx),
            ...nodeContent.slice(firstHeadingIdx + 1),
          ];
        }
      }
      const content = nodeContent?.map(n => renderNodeToHTML(n, childContext)).join('\n') || '';

      if (variant === 'quote' || variant === 'expert-quote' || variant === 'citation') {
        const titleHtml = title
          ? `<p style="margin: 0 0 1rem 0;"><strong><em>${escapeHTML(title)}</em></strong></p>\n`
          : '';
        const attributionHtml = attribution
          ? `\n<p style="margin: 1rem 0 0 0;"><em>${escapeHTML(attribution)}</em></p>`
          : '';
        return `<div${blockId} class="callout callout-${variant}"${styleAttr}>\n${titleHtml}${content}${attributionHtml}\n</div>`;
      }

      let titleHtml = '';
      if (title) {
        titleHtml = `<strong>${escapeHTML(title)}</strong>\n`;
      }

      return `<div${blockId} class="callout callout-${variant}"${styleAttr}>\n${titleHtml}${content}\n</div>`;
    },
    group: 'custom',
    content: 'block+',
  },

  // Stats grid
  statsGrid: {
    parseHTML: [{ tag: 'div.stats-grid' }],
    renderHTML: (node) => {
      const columns = node.attrs?.columns || 3;
      const style = `display: grid; grid-template-columns: repeat(${columns}, 1fr); gap: 1rem;`;
      const content = node.content?.map(n => renderNodeToHTML(n)).join('\n') || '';
      return `<div class="stats-grid" style="${style}">\n${content}\n</div>`;
    },
    group: 'custom',
    content: 'statItem+',
  },

  // Individual stat item
  statItem: {
    parseHTML: [{ tag: 'div.stat-item' }],
    renderHTML: (node) => {
      const value = node.attrs?.value || '';
      const label = node.attrs?.label || '';
      return `<div class="stat-item"><span class="stat-value">${escapeHTML(value as string)}</span><span class="stat-label">${escapeHTML(label as string)}</span></div>`;
    },
    group: 'custom',
  },

  // CTA section (matches TipTap CtaExtension node name: 'ctaBlock')
  ctaBlock: {
    parseHTML: [
      {
        tag: 'div.cta-block',
        getAttrs: (element) => {
          const button = element.querySelector('a, button');
          return {
            buttonText: button?.textContent || '',
            buttonUrl: button?.getAttribute('href') || '#',
            variant: (element.className || '').includes('secondary') ? 'secondary' : 'primary',
          };
        },
      },
      {
        tag: 'div.cta-section',
        getAttrs: (element) => {
          const button = element.querySelector('a, button');
          return {
            buttonText: button?.textContent || '',
            buttonUrl: button?.getAttribute('href') || '#',
            variant: (element.className || '').includes('secondary') ? 'secondary' : 'primary',
          };
        },
      },
    ],
    renderHTML: (node) => {
      const blockId = node.attrs?.blockId ? ` data-block-id="${node.attrs.blockId}"` : '';
      const buttonText = node.attrs?.buttonText as string;
      if (!buttonText) return '';
      const url = (node.attrs?.buttonUrl as string) || (node.attrs?.url as string) || '#';
      const variant = (node.attrs?.variant as string) || 'primary';
      const style = node.attrs?.style as string || '';
      const styleAttr = style ? ` style="${style}"` : '';
      return `<div${blockId} class="cta-block cta-${variant}"${styleAttr}>
  <a href="${escapeAttr(url)}" class="cta-button">${escapeHTML(buttonText)}</a>
</div>`;
    },
    group: 'custom',
  },

  // FAQ section (matches TipTap FaqSchemaExtension node name: 'faqSchema')
  faqSchema: {
    parseHTML: [
      { tag: 'div.faq-section' },
      { tag: 'div.faq-schema' },
      { tag: 'div[itemtype*="FAQPage"]' },
      { tag: 'section.faq' },
    ],
    renderHTML: (node) => {
      const blockId = node.attrs?.blockId ? ` data-block-id="${node.attrs.blockId}"` : '';
      const content = node.content?.map(n => renderNodeToHTML(n)).join('\n') || '';
      return `<div${blockId} class="faq-schema" style="${FAQ_SCHEMA_STYLE}" itemscope itemtype="https://schema.org/FAQPage">\n${content}\n</div>`;
    },
    group: 'custom',
    content: 'faqItem+',
  },

  // FAQ item (matches TipTap FaqItemExtension - content must be faqQuestion + faqAnswer)
  faqItem: {
    parseHTML: [
      { tag: 'div.faq-item' },
      { tag: 'div[itemtype*="Question"]' },
      { tag: 'details.faq-item' },
    ],
    renderHTML: (node) => {
      const content = node.content?.map(n => renderNodeToHTML(n)).join('\n') || '';
      return `<div class="faq-item" style="${FAQ_ITEM_STYLE}" itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">\n${content}\n</div>`;
    },
    group: 'custom',
    content: 'faqQuestion faqAnswer',
  },

  // FAQ Question (matches TipTap FaqQuestionExtension)
  faqQuestion: {
    parseHTML: [
      { tag: 'h3[itemprop="name"]' },
      { tag: 'summary.faq-question' },
      { tag: 'div.faq-question' },
    ],
    renderHTML: (node) => {
      const content = node.content?.map(n => renderNodeToHTML(n)).join('') || '';
      return `<h3 itemprop="name" class="faq-question" style="${FAQ_QUESTION_STYLE}">${content}</h3>`;
    },
    group: 'block',
    content: 'inline*',
  },

  // FAQ Answer (matches TipTap FaqAnswerExtension)
  faqAnswer: {
    parseHTML: [
      { tag: 'div[itemtype*="Answer"]' },
      { tag: 'div.faq-answer' },
    ],
    renderHTML: (node) => {
      const content = node.content?.map(n => renderNodeToHTML(n)).join('\n') || '';
      return `<div class="faq-answer" style="${FAQ_ANSWER_STYLE}" itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer"><div itemprop="text">\n${content}\n</div></div>`;
    },
    group: 'block',
    content: 'block+',
  },

  // Raw HTML node - used for injected content like CTAs that should be rendered as-is
  // The content is stored in attrs.content and rendered without any transformation
  rawHtml: {
    parseHTML: [], // Not parsed from HTML - only created programmatically
    renderHTML: (node) => {
      // Return the raw HTML content stored in attrs.content
      // This is used for CTA injection during content generation
      const content = (node.attrs?.content as string) || '';
      return content;
    },
    group: 'block',
    content: undefined, // No child content - it's raw HTML
  },
};

// =============================================================================
// HTML to RTE JSON Conversion (generateJSON)
// =============================================================================

/**
 * Generate RTE JSON from HTML string.
 * This is the main entry point for HTML → RTE conversion.
 *
 * @param html - The HTML string to convert
 * @param options - Optional configuration
 * @returns A validated RTEDocument structure
 */
export function generateRTEJSON(html: string, options?: { spacing?: PreviewSpacing }): RTEDocument {
  // Handle empty/invalid input
  if (!html || typeof html !== 'string') {
    return createEmptyDocument(options?.spacing);
  }

  const trimmedHtml = html.trim();
  if (!trimmedHtml) {
    return createEmptyDocument(options?.spacing);
  }

  traceHTMLInlineBoundaries('generateRTEJSON:entry', trimmedHtml, {
    parser: isBrowser() ? 'DOMParser' : 'fallback',
  });

  try {
    // Use server-side fallback if DOMParser not available
    if (!isBrowser()) {
      const fallback = createFallbackDocument(trimmedHtml);
      const result: RTEDocument = {
        ...fallback,
        meta: {
          spacing: options?.spacing || 'normal',
          version: '1.0.0',
        },
      };
      traceRTEDocumentWhitespace('generateRTEJSON:fallback_result', result, {
        html_length: trimmedHtml.length,
      });
      return result;
    }

    // Parse HTML using DOMParser
    const parser = new DOMParser();
    const doc = parser.parseFromString(trimmedHtml, 'text/html');

    // Check for parsing errors
    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      console.warn('RTE: HTML parsing error, attempting recovery', parserError.textContent);
      // Try wrapping in a div to help parser
      const wrappedDoc = parser.parseFromString(`<div>${trimmedHtml}</div>`, 'text/html');
      const wrapper = wrappedDoc.body.firstElementChild;
      if (wrapper) {
        const content = parseElement(wrapper);
        const result: RTEDocument = {
          type: 'doc',
          content: content.length > 0 ? content : undefined,
          meta: {
            spacing: options?.spacing || 'normal',
            version: '1.0.0',
          },
        };
        traceRTEDocumentWhitespace('generateRTEJSON:parser_recovery_result', result, {
          html_length: trimmedHtml.length,
        });
        return result;
      }
    }

    const body = doc.body;

    // Convert DOM to RTE nodes
    const content = parseElement(body);

    const result: RTEDocument = {
      type: 'doc',
      content: content.length > 0 ? content : undefined,
      meta: {
        spacing: options?.spacing || 'normal',
        version: '1.0.0',
      },
    };
    traceRTEDocumentWhitespace('generateRTEJSON:domparser_result', result, {
      html_length: trimmedHtml.length,
    });
    return result;
  } catch (error) {
    console.error('RTE: Error parsing HTML, returning empty document', error);
    return createEmptyDocument(options?.spacing);
  }
}

/**
 * Create an empty RTE document with metadata
 */
function createEmptyDocument(spacing?: PreviewSpacing): RTEDocument {
  return {
    type: 'doc',
    content: undefined,
    meta: {
      spacing: spacing || 'normal',
      version: '1.0.0',
    },
  };
}

/**
 * Parse an HTML element into RTE nodes
 */
function parseElement(element: Element): RTENode[] {
  const nodes: RTENode[] = [];
  const children = Array.from(element.childNodes);

  for (const [childIndex, child] of children.entries()) {
    if (child.nodeType === Node.TEXT_NODE) {
      const rawText = child.textContent || '';
      // Only skip text nodes that are entirely whitespace
      // Preserve text nodes with actual content, including leading/trailing spaces
      if (rawText.trim()) {
        // Normalize internal whitespace: collapse multiple spaces to single space
        // but preserve the text content itself
        const text = rawText.replace(/\s+/g, ' ');
        const inlineContext = isInlineContext(element);
        const hasPreviousContent = children
          .slice(0, childIndex)
          .some((sibling) => sibling.nodeType === Node.ELEMENT_NODE || Boolean(sibling.textContent?.trim()));
        const hasNextContent = children
          .slice(childIndex + 1)
          .some((sibling) => sibling.nodeType === Node.ELEMENT_NODE || Boolean(sibling.textContent?.trim()));
        let emittedText = text;
        if (!inlineContext) {
          if (!hasPreviousContent) emittedText = emittedText.trimStart();
          if (!hasNextContent) emittedText = emittedText.trimEnd();
        }
        if (text !== rawText || emittedText !== text || element.children.length > 0) {
          parseLogger.debug('[PARSER_TRACE] html_text_node_parse', {
            parent_tag: element.tagName.toLowerCase(),
            inline_context: inlineContext,
            has_previous_content: hasPreviousContent,
            has_next_content: hasNextContent,
            raw_text: traceSnippet(rawText, 80),
            normalized_text: traceSnippet(text, 80),
            emitted_text: traceSnippet(emittedText, 80),
            normalized_changed: text !== rawText,
            trimmed_changed: emittedText !== text,
            raw_had_leading_space: /^\s/.test(rawText),
            raw_had_trailing_space: /\s$/.test(rawText),
            emitted_has_leading_space: /^\s/.test(emittedText),
            emitted_has_trailing_space: /\s$/.test(emittedText),
          });
        }
        if (emittedText) {
          nodes.push({ type: 'text', text: emittedText });
        }
      }
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as HTMLElement;
      const tagName = el.tagName.toLowerCase();

      if (tagName === 'figure') {
        const parsedFigure = parseFigureElement(el);
        if (parsedFigure) {
          nodes.push(parsedFigure);
          continue;
        }
      }

      // Special handling for container elements not supported by TipTap - unwrap children directly
      // This preserves block IDs on child elements instead of creating invalid nodes
      const unwrapElements = ['article', 'section', 'aside', 'details', 'main', 'header', 'footer', 'nav'];
      if (unwrapElements.includes(tagName)) {
        const containerChildren = parseElement(el);
        nodes.push(...containerChildren);
      } else {
        const parsed = parseHTMLElement(el);
        if (parsed) {
          nodes.push(parsed);
        }
      }
    }
  }

  return nodes;
}

function parseFigureElement(element: HTMLElement): RTENode | null {
  const imageElement = element.querySelector('img[src]') as HTMLElement | null;
  if (!imageElement) {
    return null;
  }

  const imageNode = parseHTMLElement(imageElement);
  if (!imageNode || imageNode.type !== 'image') {
    return null;
  }

  const captionText = element.querySelector('figcaption')?.textContent?.replace(/\s+/g, ' ').trim();
  const attrs = { ...(imageNode.attrs || {}) };

  if (captionText) {
    attrs.caption = captionText;
  }

  const figureBlockId = element.getAttribute('data-block-id');
  if (figureBlockId && !attrs.blockId) {
    attrs.blockId = figureBlockId;
  }

  return {
    ...imageNode,
    attrs: Object.keys(attrs).length > 0 ? attrs : undefined,
  };
}

/**
 * Check if an element is in an inline context where whitespace should be preserved
 */
function isInlineContext(element: Element): boolean {
  const inlineElements = ['span', 'a', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'strike', 'code', 'mark', 'small', 'sub', 'sup'];
  return inlineElements.includes(element.tagName.toLowerCase());
}

/**
 * Parse a single HTML element into an RTE node
 */
function parseHTMLElement(element: HTMLElement): RTENode | null {
  const tagName = element.tagName.toLowerCase();
  const className = element.className?.toLowerCase() || '';
  const style = element.getAttribute('style') || '';
  const itemType = element.getAttribute('itemtype') || '';

  // Try to match against node definitions
  for (const [nodeType, definition] of Object.entries(NODE_DEFINITIONS)) {
    for (const rule of definition.parseHTML) {
      // Check tag match
      if (rule.tag.includes('.')) {
        // Tag with class: "div.key-takeaways"
        const [tag, cls] = rule.tag.split('.');
        if (tagName !== tag || !className.includes(cls)) continue;
      } else if (rule.tag.includes('[')) {
        // Tag with attribute: "div[itemtype*='FAQPage']" or "img[src]"
        const match = rule.tag.match(/(\w+)\[([^\]]+)\]/);
        if (!match) continue;
        const [, tag, attrSelector] = match;
        if (tagName !== tag) continue;
        // Parse attribute selector (supports: attr, attr=value, attr*=value)
        if (attrSelector.includes('=')) {
          const attrMatch = attrSelector.match(/(\w+)\*?=["']?([^"'\]]+)["']?/);
          if (attrMatch) {
            const [, attrName, attrValue] = attrMatch;
            const actualValue = element.getAttribute(attrName);
            if (!actualValue?.includes(attrValue)) continue;
          }
        } else {
          // Just checking attribute existence: "img[src]"
          if (!element.hasAttribute(attrSelector)) continue;
        }
      } else {
        // Simple tag match
        if (tagName !== rule.tag) continue;
      }

      // Get attributes from element
      const attrsResult = rule.getAttrs?.(element);
      if (attrsResult === false) continue;
      const attrs = attrsResult || {};

      // Add common attributes
      if (style) attrs.style = style;
      if (className) attrs.class = className;

      // Preserve block ID for roundtrip capability
      const blockId = element.getAttribute('data-block-id');
      if (blockId) {
        attrs.blockId = blockId;
      }

      // Parse children
      const content = parseElement(element);

      return {
        type: nodeType,
        attrs: Object.keys(attrs).length > 0 ? attrs : undefined,
        content: content.length > 0 ? content : undefined,
      };
    }
  }

  // Handle special cases not covered by definitions

  // Inline elements with marks
  if (['strong', 'b'].includes(tagName)) {
    const content = parseElement(element);
    traceInlineMarkParse(tagName, content, { type: 'bold' });
    return wrapWithMark(content, { type: 'bold' });
  }
  if (['em', 'i'].includes(tagName)) {
    const content = parseElement(element);
    traceInlineMarkParse(tagName, content, { type: 'italic' });
    return wrapWithMark(content, { type: 'italic' });
  }
  if (tagName === 'u') {
    const content = parseElement(element);
    return wrapWithMark(content, { type: 'underline' });
  }
  if (['s', 'del', 'strike'].includes(tagName)) {
    const content = parseElement(element);
    return wrapWithMark(content, { type: 'strike' });
  }
  if (tagName === 'code' && !element.parentElement?.tagName?.toLowerCase().includes('pre')) {
    const content = parseElement(element);
    return wrapWithMark(content, { type: 'code' });
  }
  if (tagName === 'a') {
    const content = parseElement(element);
    const href = element.getAttribute('href');
    traceInlineMarkParse(tagName, content, {
      type: 'link',
      attrs: { href, target: element.getAttribute('target') },
    });
    return wrapWithMark(content, {
      type: 'link',
      attrs: { href, target: element.getAttribute('target') },
    });
  }

  // Styled divs - try to detect callout types
  if (tagName === 'div') {
    const variant = detectCalloutVariant(element, className, style);
    if (variant) {
      const title = element.querySelector('strong, h4, h3')?.textContent || '';
      const content = parseElement(element);
      return {
        type: 'callout',
        attrs: { variant, title, style, class: className },
        content,
      };
    }

    // Stats grid detection
    if (className.includes('stats') || style.includes('grid')) {
      const statItems = element.querySelectorAll('[class*="stat"]');
      if (statItems.length > 0) {
        const items: RTENode[] = Array.from(statItems).map(item => {
          const value = item.querySelector('.stat-value, strong')?.textContent || '';
          const label = item.querySelector('.stat-label, span:not(.stat-value)')?.textContent || '';
          return { type: 'statItem', attrs: { value, label } };
        });
        return {
          type: 'statsGrid',
          attrs: { columns: Math.min(items.length, 4) },
          content: items,
        };
      }
    }

    // CTA detection
    if (className.includes('cta')) {
      const button = element.querySelector('a, button');
      return {
        type: 'ctaBlock',
        attrs: {
          buttonText: button?.textContent || '',
          buttonUrl: button?.getAttribute('href') || '#',
          variant: className.includes('secondary') ? 'secondary' : 'primary',
          style,
        },
      };
    }

    // FAQ detection
    if (className.includes('faq') || itemType.includes('FAQPage')) {
      const faqItems = element.querySelectorAll('.faq-item, [itemtype*="Question"]');
      const items: RTENode[] = Array.from(faqItems).map(item => {
        const questionText = item.querySelector('h3, [itemprop="name"]')?.textContent || 'Question';
        const answerEl = item.querySelector('[itemprop="text"], .faq-answer, p') as HTMLElement;
        const answerContent = answerEl ? parseElement(answerEl) : [{ type: 'paragraph', content: [{ type: 'text', text: 'Answer' }] }];

        // Create proper faqQuestion and faqAnswer structure for TipTap
        return {
          type: 'faqItem',
          content: [
            {
              type: 'faqQuestion',
              content: [{ type: 'text', text: questionText }],
            },
            {
              type: 'faqAnswer',
              content: answerContent.length > 0 ? answerContent : [{ type: 'paragraph', content: [{ type: 'text', text: 'Answer' }] }],
            },
          ],
        };
      });
      return {
        type: 'faqSchema',
        content: items.length > 0 ? items : undefined,
      };
    }

    // Generic container - parse children instead
    const content = parseElement(element);
    if (content.length === 1) {
      return content[0];
    }
    if (content.length > 0) {
      // Wrap in a paragraph if mixed content
      return { type: 'paragraph', content };
    }
  }

  // Span with styles
  if (tagName === 'span') {
    const content = parseElement(element);
    const normalizedStyle = normalizeTextStyleString(style);
    if (normalizedStyle) {
      // Extract style marks
      const marks: RTEMark[] = [];
      if (normalizedStyle.includes('font-weight: bold') || normalizedStyle.includes('font-weight:bold')) {
        marks.push({ type: 'bold' });
      }
      if (normalizedStyle.includes('font-style: italic') || normalizedStyle.includes('font-style:italic')) {
        marks.push({ type: 'italic' });
      }
      if (
        normalizedStyle.includes('text-decoration: underline') ||
        normalizedStyle.includes('text-decoration:underline')
      ) {
        marks.push({ type: 'underline' });
      }
      if (marks.length > 0) {
        return wrapWithMarks(content, marks);
      }
      // Keep style as textStyle mark
      if (content.length > 0) {
        return wrapWithMark(content, { type: 'textStyle', attrs: { style: normalizedStyle } });
      }
    }
    // Pass through content
    if (content.length === 1) return content[0];
    if (content.length > 1) return { type: 'paragraph', content };
  }

  // Note: Article wrapper is handled in parseElement to unwrap children directly

  // Nav (often TOC) - treat as list
  if (tagName === 'nav') {
    const items = Array.from(element.querySelectorAll('li, a'));
    if (items.length > 0) {
      const listItems = items.map(item => ({
        type: 'listItem',
        content: [{ type: 'paragraph', content: [{ type: 'text', text: item.textContent || '' }] }],
      }));
      return { type: 'orderedList', content: listItems };
    }
  }

  // Fallback: Table - convert to bullet list (TipTap table extension not installed)
  // This preserves table content in a readable format
  if (tagName === 'table') {
    const rows = element.querySelectorAll('tr');
    const listItems: RTENode[] = [];

    rows.forEach((row, rowIndex) => {
      const cells = row.querySelectorAll('th, td');
      const cellTexts: string[] = [];
      cells.forEach(cell => {
        const text = cell.textContent?.trim();
        if (text) cellTexts.push(text);
      });
      if (cellTexts.length > 0) {
        const rowText = rowIndex === 0 && row.querySelector('th')
          ? `**${cellTexts.join(' | ')}**` // Bold header row
          : cellTexts.join(' | ');
        listItems.push({
          type: 'listItem',
          content: [{ type: 'paragraph', content: [{ type: 'text', text: rowText }] }],
        });
      }
    });

    if (listItems.length > 0) {
      const blockId = element.getAttribute('data-block-id');
      return {
        type: 'bulletList',
        attrs: blockId ? { blockId } : undefined,
        content: listItems,
      };
    }
  }

  // Fallback: Figcaption - convert to italic paragraph
  if (tagName === 'figcaption') {
    const text = element.textContent?.trim();
    if (text) {
      return {
        type: 'paragraph',
        content: [{
          type: 'text',
          text,
          marks: [{ type: 'italic' }],
        }],
      };
    }
  }

  // Fallback: thead, tbody, tr - unwrap and parse children
  if (['thead', 'tbody', 'tr', 'th', 'td'].includes(tagName)) {
    const content = parseElement(element);
    if (content.length === 1) return content[0];
    if (content.length > 0) return { type: 'paragraph', content };
  }

  return null;
}

/**
 * Detect callout variant from element properties
 * Supports three detection methods:
 * 1. CSS class-based detection
 * 2. Inline style color-based detection
 * 3. Content-based detection (emoji + text markers)
 */
function detectCalloutVariant(element: HTMLElement, className: string, style: string): string | null {
  // Class-based detection
  if (className.includes('table-of-contents')) return 'table-of-contents';
  if (className.includes('key-takeaways') || className.includes('takeaways')) return 'key-takeaways';
  if (className.includes('quick-picks') || className.includes('quick-answers')) return 'quick-picks';
  if (className.includes('pro-tip') || className.includes('tip-box')) return 'pro-tip';
  if (className.includes('warning') || className.includes('alert')) return 'warning';
  if (className.includes('verdict')) return 'verdict';
  if (className.includes('info') || className.includes('note')) return 'info';
  if (className.includes('success')) return 'success';

  // Style-based detection (color patterns)
  const s = style.toLowerCase();
  if (s.includes('fef3c7') || s.includes('f59e0b')) return 'pro-tip'; // Yellow/Amber
  if (s.includes('fef2f2') || s.includes('ef4444')) return 'warning'; // Red
  if (s.includes('f0f9ff') || s.includes('0ea5e9')) return 'key-takeaways'; // Legacy light blue
  if (s.includes('f8fafc') && s.includes('e2e8f0') && s.includes('3b82f6')) return 'key-takeaways'; // Neutral box with blue accent
  if (s.includes('e2e8f0') && s.includes('border-top') && s.includes('border-bottom')) return 'table-of-contents'; // Compact ToC
  if (s.includes('eff6ff') || s.includes('3b82f6')) return 'verdict'; // Blue
  if (s.includes('f0fdf4') || s.includes('22c55e')) return 'success'; // Green
  if (s.includes('6366f1')) return 'expert-quote'; // Purple
  if (s.includes('1e293b') || s.includes('gradient')) return 'rating'; // Dark

  // Content-based detection (emoji + text markers)
  // Get text content from the element (first 200 chars for efficiency)
  const textContent = (element.textContent || '').slice(0, 200).toLowerCase();
  const rawContent = (element.textContent || '').slice(0, 200); // Keep original case for emoji

  // Pro Tip / Tip detection
  if (rawContent.includes('💡') || textContent.includes('pro tip') || textContent.includes('**pro tip')) {
    return 'pro-tip';
  }

  // Warning detection
  if (rawContent.includes('⚠️') || rawContent.includes('⚠') || textContent.includes('warning:') || textContent.includes('**warning')) {
    return 'warning';
  }

  // Key Takeaways detection
  if (rawContent.includes('📋') || rawContent.includes('🔑') || textContent.includes('key takeaway') || textContent.includes('**key takeaway')) {
    return 'key-takeaways';
  }

  // Success / Check detection
  if (rawContent.includes('✅') || rawContent.includes('✓') || textContent.includes('**success')) {
    return 'success';
  }

  // Info / Note detection
  if (rawContent.includes('ℹ️') || rawContent.includes('📝') || textContent.includes('**note:') || textContent.includes('**info:')) {
    return 'info';
  }

  // Expert quote detection
  if (rawContent.includes('💬') || rawContent.includes('🗣') || textContent.includes('expert quote') || textContent.includes('**expert')) {
    return 'expert-quote';
  }

  return null;
}

/**
 * Recursively apply a mark to all text nodes at any depth
 * @param node - The node to process
 * @param mark - The mark to apply
 * @param maxDepth - Maximum recursion depth to prevent stack overflow (default: 50)
 * @returns The node with marks applied to all nested text nodes
 */
function applyMarkRecursively(node: RTENode, mark: RTEMark, maxDepth: number = 50): RTENode {
  if (maxDepth <= 0) {
    console.warn('RTE: Max recursion depth reached in applyMarkRecursively');
    return node;
  }

  if (node.type === 'text') {
    return {
      ...node,
      marks: [...(node.marks || []), mark],
    };
  }

  if (node.content && node.content.length > 0) {
    return {
      ...node,
      content: node.content.map(child => applyMarkRecursively(child, mark, maxDepth - 1)),
    };
  }

  return node;
}

/**
 * Recursively apply multiple marks to all text nodes at any depth
 */
function applyMarksRecursively(node: RTENode, marks: RTEMark[], maxDepth: number = 50): RTENode {
  if (maxDepth <= 0) {
    console.warn('RTE: Max recursion depth reached in applyMarksRecursively');
    return node;
  }

  if (node.type === 'text') {
    return {
      ...node,
      marks: [...(node.marks || []), ...marks],
    };
  }

  if (node.content && node.content.length > 0) {
    return {
      ...node,
      content: node.content.map(child => applyMarksRecursively(child, marks, maxDepth - 1)),
    };
  }

  return node;
}

/**
 * Wrap content nodes with a mark (applies to all nested text nodes)
 */
function wrapWithMark(content: RTENode[], mark: RTEMark): RTENode | null {
  if (content.length === 0) return null;

  // Apply mark recursively to all text nodes at any depth
  const marked = content.map(node => applyMarkRecursively(node, mark));

  return marked.length === 1 ? marked[0] : { type: 'paragraph', content: marked };
}

/**
 * Wrap content with multiple marks (applies to all nested text nodes)
 */
function wrapWithMarks(content: RTENode[], marks: RTEMark[]): RTENode | null {
  if (content.length === 0) return null;

  // Apply all marks recursively to all text nodes at any depth
  const marked = content.map(node => applyMarksRecursively(node, marks));

  return marked.length === 1 ? marked[0] : { type: 'paragraph', content: marked };
}

// =============================================================================
// RTE JSON to HTML Conversion (generateHTML)
// =============================================================================

/**
 * Generate HTML from RTE JSON document.
 *
 * **AUTHORITATIVE SOURCE OF TRUTH**
 *
 * This function is the single source of truth for converting TipTap/ProseMirror
 * JSON documents to HTML. The backend Python implementation should produce
 * identical output for the same input to ensure consistency between:
 * - Frontend preview rendering
 * - Backend publish-time HTML generation
 * - WordPress content publishing
 *
 * When publishing content to WordPress, HTML MUST be generated from `content_json`
 * using this function (or its backend equivalent) to ensure the published content
 * matches what the user sees in the TipTap editor.
 *
 * @param doc - TipTap/ProseMirror document JSON (RTEDocument)
 * @returns HTML string suitable for WordPress publishing
 *
 * @example
 * ```typescript
 * import { generateHTMLFromRTE } from '@/components/blog-templates';
 *
 * // Generate HTML for publishing
 * const html = generateHTMLFromRTE(content.content_json);
 * ```
 *
 * @see RTEDocument - The expected input document structure
 * @see generateRTEJSON - The inverse function (HTML to JSON)
 */
export function generateHTMLFromRTE(doc: RTEDocument, siteHost?: string | null): string {
  if (!doc || doc.type !== 'doc' || !doc.content) {
    return '';
  }

  // Set the tenant host for this (fully synchronous) render pass so the leaf link
  // renderers can classify internal vs external links. Restored in `finally`.
  const previousSiteHost = activeSiteHost;
  activeSiteHost = siteHost || undefined;

  try {
    traceRTEDocumentWhitespace('generateHTMLFromRTE:input_doc', doc);

    const result = doc.content.map((node, index) => {
      const html = renderNodeToHTML(node);
      if (node.type === 'faqSchema' && !isFaqHeading(doc.content?.[index - 1])) {
        return `<h2>Frequently Asked Questions</h2>\n\n${html}`;
      }
      return html;
    }).join('\n\n');

    traceHTMLInlineBoundaries('generateHTMLFromRTE:output_html', result, {
      top_level_nodes: doc.content.length,
    });

    return result;
  } finally {
    activeSiteHost = previousSiteHost;
  }
}

function isFaqHeading(node: RTENode | undefined): boolean {
  if (!node || node.type !== 'heading') return false;
  const level = (node.attrs?.level as number) || 1;
  if (level !== 2) return false;
  const text = extractPlainTextFromNode(node).trim().toLowerCase();
  // Prefix match: legacy/already-published documents may carry a topic-suffixed
  // FAQ heading (e.g. "Frequently Asked Questions About X") — treat those as
  // the FAQ heading too so the generic path never injects a second bare H2.
  return text.startsWith('frequently asked questions') || text === 'faq' || text === 'faqs';
}

function extractPlainTextFromNode(node: RTENode): string {
  if (node.text) return node.text;
  return node.content?.map(extractPlainTextFromNode).join('') || '';
}

/**
 * Render a single RTE node to HTML
 */
function renderNodeToHTML(node: RTENode, context?: RenderHTMLContext): string {
  const definition = NODE_DEFINITIONS[node.type];
  if (definition) {
    return definition.renderHTML(node, context);
  }

  // Fallback for unknown node types
  console.warn(`Unknown RTE node type: ${node.type}`);
  if (node.content) {
    return node.content.map(n => renderNodeToHTML(n, context)).join('');
  }
  if (node.text) {
    return escapeHTML(node.text);
  }
  return '';
}

/**
 * Map of TipTap textStyle mark attributes to CSS property names.
 * Used to convert textStyle mark attrs to inline CSS.
 */
const TEXT_STYLE_ATTR_TO_CSS: Record<string, string> = {
  color: 'color',
  backgroundColor: 'background-color',
  fontSize: 'font-size',
  fontFamily: 'font-family',
};

const THEME_INHERITED_TEXT_COLORS = new Set([BODY_TEXT_COLOR, HEADING_TEXT_COLOR, '#374151']);

/**
 * Build inline CSS from textStyle mark attributes.
 * Converts { color: '#ff0000', fontSize: '1.5rem' } to "color: #ff0000; font-size: 1.5rem"
 */
function buildTextStyleCSS(attrs: Record<string, unknown> | undefined): string {
  if (!attrs) return '';

  const styleParts: string[] = [];

  // First check for pre-built style string
  if (attrs.style && typeof attrs.style === 'string') {
    const normalizedStyle = normalizeTextStyleString(attrs.style);
    if (normalizedStyle) styleParts.push(normalizedStyle);
  }

  // Then convert individual attributes to CSS
  for (const [attrName, cssProperty] of Object.entries(TEXT_STYLE_ATTR_TO_CSS)) {
    const value = normalizeTextColor(attrs[attrName], cssProperty);
    if (value !== undefined && value !== null && value !== '') {
      styleParts.push(`${cssProperty}: ${value}`);
    }
  }

  return styleParts.join('; ');
}

function normalizeTextColor(value: unknown, cssProperty: string): unknown {
  if (cssProperty !== 'color' || typeof value !== 'string') return value;

  return THEME_INHERITED_TEXT_COLORS.has(value.trim().toLowerCase()) ? undefined : value;
}

function normalizeTextStyleString(style: string): string {
  return style
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((declaration) => {
      const separatorIndex = declaration.indexOf(':');
      if (separatorIndex === -1) return declaration;

      const property = declaration.slice(0, separatorIndex).trim();
      const value = declaration.slice(separatorIndex + 1).trim();
      const normalizedValue = normalizeTextColor(value, property.toLowerCase());
      return normalizedValue ? `${property}: ${normalizedValue}` : '';
    })
    .filter(Boolean)
    .join('; ');
}

/**
 * Build inline CSS for links - adds explicit link styling (blue + underline)
 * to ensure links are visually distinct regardless of WordPress theme.
 * Also keeps other styles like fontSize that don't affect link visibility.
 *
 * NOTE: The #2563eb blue is intentionally hardcoded here (NOT a theme token).
 * This generates inline HTML published to external WordPress sites where our
 * app theme tokens don't exist. A standard link-blue ensures visibility
 * across any WordPress theme.
 */
function buildTextStyleCSSForLink(attrs: Record<string, unknown> | undefined): string {
  // Always start with link styling to ensure visibility
  // Hardcoded blue for WordPress output — see function docstring
  const styleParts: string[] = ['color: #2563eb', 'text-decoration: underline'];

  if (!attrs) return styleParts.join('; ');

  // Add non-color styles for links
  // Skip: color (we already set it to blue above)
  // Keep: fontSize, fontFamily, backgroundColor (highlight)
  const linkSafeAttrs: Record<string, string> = {
    fontSize: 'font-size',
    fontFamily: 'font-family',
    backgroundColor: 'background-color',
  };

  for (const [attrName, cssProperty] of Object.entries(linkSafeAttrs)) {
    const value = attrs[attrName];
    if (value !== undefined && value !== null && value !== '') {
      styleParts.push(`${cssProperty}: ${value}`);
    }
  }

  return styleParts.join('; ');
}

function applyMark(text: string, mark: RTEMark): string {
  switch (mark.type) {
    case 'bold':
      return `<strong>${text}</strong>`;
    case 'italic':
      return `<em>${text}</em>`;
    case 'underline':
      return `<u>${text}</u>`;
    case 'strike':
      return `<s>${text}</s>`;
    case 'code':
      return `<code>${text}</code>`;
    case 'link': {
      const href = mark.attrs?.href || '#';
      // Internal ⇒ strip any stamped target/rel; external ⇒ unchanged (_blank + nofollow).
      const isExternal = !isInternalHref(href, activeSiteHost);
      const target = isExternal ? ' target="_blank"' : '';
      const rel = isExternal ? ' rel="nofollow noopener noreferrer"' : '';
      // Hardcoded blue for WordPress output — not an app theme color (see buildTextStyleCSSForLink)
      const linkStyle = 'color: #2563eb; text-decoration: underline;';
      return `<a href="${escapeAttr(href as string)}"${target}${rel} style="${linkStyle}">${text}</a>`;
    }
    case 'textStyle': {
      // Build CSS from individual textStyle attributes (color, fontSize, etc.)
      const styleCSS = buildTextStyleCSS(mark.attrs);
      if (styleCSS) {
        return `<span style="${escapeAttr(styleCSS)}">${text}</span>`;
      }
      return text;
    }
    default:
      return text;
  }
}

// =============================================================================
// Template Builder Integration
// =============================================================================

/**
 * Convert Template Builder blocks to RTE JSON document
 */
export function templateBlocksToRTEJSON(
  blocks: TemplateBlock[],
  options?: { spacing?: PreviewSpacing }
): RTEDocument {
  const content: RTENode[] = blocks.map(block => templateBlockToRTENode(block)).filter(Boolean) as RTENode[];

  return {
    type: 'doc',
    content,
    meta: {
      spacing: options?.spacing || 'normal',
      version: '1.0.0',
    },
  };
}

// =============================================================================
// Blocks With Content Conversion (Backend → TipTap JSON)
// =============================================================================

/**
 * Generated content structure from backend (matches GeneratedBlockContent model)
 */
export interface GeneratedContent {
  block_id: string;
  block_type: string;
  text_content?: string;
  list_items?: string[];
  faq_items?: Array<{ question: string; answer: string }>;
  stat_items?: Array<{ value: string; label: string }>;
  table_headers?: string[];
  table_rows?: string[][];
  cta_text?: string;
  cta_description?: string;
  image_url?: string;
  image_alt?: string;
  image_title?: string;
  image_caption?: string;
  title?: string;
  items?: string[];
  attribution?: string;
  generated?: boolean;
  error?: string;
}

/**
 * Block with generated content from backend
 */
export interface BlockWithContent {
  id: string;
  type: string;
  props?: Record<string, unknown>;
  generated_content?: GeneratedContent;
}

/**
 * Parse markdown bold (**text**) into text nodes with bold marks
 * Returns array of nodes, some with bold marks, some without
 */
function parseBoldMarkdown(text: string, existingMarks: RTEMark[] = []): RTENode[] {
  const nodes: RTENode[] = [];
  // Match **text** (non-greedy, no nested asterisks)
  const boldPattern = /\*\*([^*]+)\*\*/g;
  let lastEnd = 0;
  let match;

  while ((match = boldPattern.exec(text)) !== null) {
    // Add text before the bold marker
    if (match.index > lastEnd) {
      const plainText = text.slice(lastEnd, match.index);
      if (plainText) {
        if (existingMarks.length > 0) {
          nodes.push({ type: 'text', text: plainText, marks: [...existingMarks] });
        } else {
          nodes.push({ type: 'text', text: plainText });
        }
      }
    }

    // Add the bold text with bold mark + any existing marks
    const boldText = match[1];
    const boldMarks: RTEMark[] = [{ type: 'bold' }, ...existingMarks];
    nodes.push({
      type: 'text',
      text: boldText,
      marks: boldMarks,
    });

    lastEnd = match.index + match[0].length;
  }

  // Add remaining text
  if (lastEnd < text.length) {
    const remaining = text.slice(lastEnd);
    if (remaining) {
      if (existingMarks.length > 0) {
        nodes.push({ type: 'text', text: remaining, marks: [...existingMarks] });
      } else {
        nodes.push({ type: 'text', text: remaining });
      }
    }
  }

  // If no bold found, return single text node
  if (nodes.length === 0 && text) {
    if (existingMarks.length > 0) {
      nodes.push({ type: 'text', text, marks: [...existingMarks] });
    } else {
      nodes.push({ type: 'text', text });
    }
  }

  return nodes;
}

/**
 * Convert markdown links [text](url) and bold (**text**) to TipTap text nodes with marks
 * Processing order: links first, then bold within each segment
 */
function textToNodes(text: string): RTENode[] {
  const nodes: RTENode[] = [];
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastEnd = 0;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    // Add text before the link (may contain bold)
    if (match.index > lastEnd) {
      let plainText = text.slice(lastEnd, match.index);
      if (plainText && !/\s$/.test(plainText)) {
        plainText += ' ';
      }
      if (plainText) {
        // Parse bold markers in non-link text
        const boldParsedNodes = parseBoldMarkdown(plainText);
        nodes.push(...boldParsedNodes);
      }
    }

    // Add the link - also check for bold inside link text
    const linkText = match[1];
    const linkUrl = match[2];
    const linkMark: RTEMark = { type: 'link', attrs: { href: linkUrl } };

    // Parse bold within link text, passing link mark as existing mark
    const linkNodes = parseBoldMarkdown(linkText, [linkMark]);
    nodes.push(...linkNodes);

    lastEnd = match.index + match[0].length;
  }

  // Add remaining text (may contain bold)
  if (lastEnd < text.length) {
    let remaining = text.slice(lastEnd);
    if (remaining && !/^\s/.test(remaining) && !/^[.,;:!?)]/.test(remaining)) {
      remaining = ` ${remaining}`;
    }
    if (remaining) {
      const boldParsedNodes = parseBoldMarkdown(remaining);
      nodes.push(...boldParsedNodes);
    }
  }

  // If no links found, parse for bold markers
  if (nodes.length === 0 && text) {
    const boldParsedNodes = parseBoldMarkdown(text);
    nodes.push(...boldParsedNodes);
  }

  return nodes;
}

/**
 * Convert blocks_with_content from backend to TipTap JSON document
 * This is the primary conversion function for the JSON-first architecture.
 */
export function blocksWithContentToRTEJSON(
  blocks: BlockWithContent[],
  options?: { spacing?: PreviewSpacing }
): RTEDocument {
  const content: RTENode[] = blocks
    .map(block => blockWithContentToRTENode(block))
    .filter((node): node is RTENode => node !== null);

  return {
    type: 'doc',
    content: content.length > 0 ? content : undefined,
    meta: {
      spacing: options?.spacing || 'normal',
      version: '1.0.0',
    },
  };
}

/**
 * Build text marks from block styling props
 */
function buildTextMarksFromProps(props: Record<string, unknown>): RTEMark[] {
  const marks: RTEMark[] = [];

  // Bold
  const fontWeight = props.fontWeight as string;
  if (fontWeight === 'bold' || fontWeight === '700' || fontWeight === 'semibold' || fontWeight === '600') {
    marks.push({ type: 'bold' });
  }

  // Italic
  if (props.fontStyle === 'italic') {
    marks.push({ type: 'italic' });
  }

  // Underline / Strikethrough
  const textDecoration = props.textDecoration as string;
  if (textDecoration === 'underline') {
    marks.push({ type: 'underline' });
  } else if (textDecoration === 'line-through') {
    marks.push({ type: 'strike' });
  }

  // TextStyle mark for color, fontSize, backgroundColor
  const textStyleAttrs: Record<string, string> = {};
  const normalizedColor = normalizeTextColor(props.textColor || props.color, 'color');
  if (typeof normalizedColor === 'string' && normalizedColor) {
    textStyleAttrs.color = normalizedColor;
  }
  if (props.fontSize) {
    textStyleAttrs.fontSize = props.fontSize as string;
  }
  if (props.highlightColor || props.backgroundColor) {
    textStyleAttrs.backgroundColor = (props.highlightColor || props.backgroundColor) as string;
  }

  if (Object.keys(textStyleAttrs).length > 0) {
    marks.push({ type: 'textStyle', attrs: textStyleAttrs });
  }

  return marks;
}

/**
 * Convert text to nodes with optional marks applied
 */
function textToNodesWithMarks(text: string, marks: RTEMark[]): RTENode[] {
  const nodes = textToNodes(text);
  if (marks.length === 0) return nodes;

  // Apply marks to all text nodes
  return nodes.map(node => {
    if (node.type === 'text') {
      const existingMarks = node.marks || [];
      return { ...node, marks: [...marks, ...existingMarks] };
    }
    return node;
  });
}

/**
 * Convert a single block with generated content to an RTE node
 */
function blockWithContentToRTENode(block: BlockWithContent): RTENode | null {
  const { type, props, generated_content } = block;
  const p = (props || {}) as Record<string, unknown>;
  const gc = generated_content;

  // Build common attrs with blockId for roundtrip
  const baseAttrs: Record<string, unknown> = { blockId: block.id };

  // Apply node-level styling (textAlign)
  if (p.textAlign) {
    baseAttrs.textAlign = p.textAlign;
  }

  // Build text marks from styling props
  const textMarks = buildTextMarksFromProps(p);

  // Skip blocks without generated content (unless they're static/constant)
  if (!gc?.generated && !p.constant_value) {
    return null;
  }

  switch (type) {
    case 'heading':
    case 'h1':
    case 'h2':
    case 'h3': {
      const level = type === 'heading'
        ? (p.level as number) || 2
        : parseInt(type.charAt(1));
      // Skip H1 - WordPress post title serves as H1
      if (level === 1) return null;
      return {
        type: 'heading',
        attrs: { ...baseAttrs, level },
        content: gc?.text_content ? textToNodesWithMarks(gc.text_content, textMarks) : undefined,
      };
    }

    case 'paragraph':
      return {
        type: 'paragraph',
        attrs: baseAttrs,
        content: gc?.text_content ? textToNodesWithMarks(gc.text_content, textMarks) : undefined,
      };

    case 'bulletList':
    case 'orderedList':
    case 'list':
    case 'bullet-list':
    case 'numbered-list': {
      const isLettered = p.listType === 'lettered' || p.listStyle === 'lettered' || p.type === 'a';
      const isOrdered = isLettered || type === 'orderedList' || type === 'numbered-list' || p.listStyle === 'numbered';
      const items = gc?.list_items || [];
      const attrs = isLettered ? { ...baseAttrs, type: 'a' } : baseAttrs;
      return {
        type: isOrdered ? 'orderedList' : 'bulletList',
        attrs,
        content: items.map(item => ({
          type: 'listItem',
          content: [{ type: 'paragraph', content: textToNodesWithMarks(item, textMarks) }],
        })),
      };
    }

    case 'blockquote':
      return {
        type: 'blockquote',
        attrs: { ...baseAttrs, attribution: gc?.attribution || p.attribution },
        content: gc?.text_content
          ? [{ type: 'paragraph', content: textToNodesWithMarks(gc.text_content, textMarks) }]
          : undefined,
      };

    case 'callout':
    case 'key-takeaways':
    case 'pro-tip':
    case 'warning': {
      const variant = type === 'callout' ? (p.variant as string) || 'callout' : type;
      const innerContent: RTENode[] = [];

      // Add title
      const title = gc?.title || (p.title as string);
      if (title) {
        innerContent.push({
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: title }],
        });
      }

      // Key takeaways has list items
      if ((variant === 'key-takeaways' || type === 'key-takeaways') && gc?.items) {
        innerContent.push({
          type: 'bulletList',
          content: gc.items.map(item => ({
            type: 'listItem',
            content: [{ type: 'paragraph', content: textToNodesWithMarks(item, textMarks) }],
          })),
        });
      } else if (gc?.text_content) {
        // Pro-tip/warning have text content
        innerContent.push({
          type: 'paragraph',
          content: textToNodesWithMarks(gc.text_content, textMarks),
        });
      }

      return {
        type: 'callout',
        attrs: { ...baseAttrs, variant, stylePreset: p.stylePreset || variant },
        content: innerContent.length > 0 ? innerContent : undefined,
      };
    }

    case 'faq':
      return {
        type: 'faqSchema',
        attrs: baseAttrs,
        content: (gc?.faq_items || []).map(item => ({
          type: 'faqItem',
          content: [
            {
              type: 'faqQuestion',
              content: textToNodesWithMarks(item.question, textMarks),
            },
            {
              type: 'faqAnswer',
              content: [{ type: 'paragraph', content: textToNodesWithMarks(item.answer, textMarks) }],
            },
          ],
        })),
      };

    case 'stats':
      return {
        type: 'statsGrid',
        attrs: { ...baseAttrs, columns: (p.columns as number) || (gc?.stat_items?.length || 3) },
        content: (gc?.stat_items || []).map(stat => ({
          type: 'statItem',
          attrs: { value: stat.value, label: stat.label },
        })),
      };

    case 'cta':
      return {
        type: 'ctaBlock',
        attrs: {
          ...baseAttrs,
          title: p.title as string,
          description: gc?.cta_description || '',
          buttonText: gc?.cta_text || (p.buttonText as string) || '',
          buttonUrl: (p.url as string) || '#',
          variant: (p.style as string) || 'primary',
        },
      };

    case 'image':
    case 'ai-image': {
      const isAIGenerated = type === 'ai-image';
      const url = gc?.image_url;
      if (!url) {
        // Placeholder for pending image
        return {
          type: 'paragraph',
          attrs: baseAttrs,
          content: [{ type: 'text', text: '[Image placeholder - generation pending]' }],
        };
      }
      return {
        type: 'image',
        attrs: {
          ...baseAttrs,
          src: url,
          alt: gc?.image_alt || '',
          title: gc?.image_title || '',
          caption: gc?.image_caption || '',
          isAIGenerated,
        },
      };
    }

    case 'table': {
      // Build proper table structure from generated content
      const headers = gc?.table_headers || [];
      const rows = gc?.table_rows || [];

      const tableContent: RTENode[] = [];

      // Add header row if present
      if (headers.length > 0) {
        tableContent.push({
          type: 'tableRow',
          content: headers.map(header => ({
            type: 'tableHeader',
            content: [{ type: 'paragraph', content: [{ type: 'text', text: header }] }],
          })),
        });
      }

      // Add data rows
      for (const row of rows) {
        tableContent.push({
          type: 'tableRow',
          content: row.map(cell => ({
            type: 'tableCell',
            content: [{ type: 'paragraph', content: [{ type: 'text', text: cell }] }],
          })),
        });
      }

      return {
        type: 'table',
        attrs: baseAttrs,
        content: tableContent,
      };
    }

    case 'divider':
    case 'horizontalRule':
      return {
        type: 'horizontalRule',
        attrs: baseAttrs,
      };

    default:
      // Fallback: wrap text in paragraph with styling applied
      if (gc?.text_content) {
        return {
          type: 'paragraph',
          attrs: baseAttrs,
          content: textToNodesWithMarks(gc.text_content, textMarks),
        };
      }
      return null;
  }
}

/**
 * Convert a single Template Builder block to an RTE node
 */
function templateBlockToRTENode(block: TemplateBlock): RTENode | null {
  const { type, props } = block;
  const p = props as Record<string, unknown>;

  switch (type) {
    case 'h1':
    case 'h2':
    case 'h3':
      return {
        type: 'heading',
        attrs: { level: parseInt(type.charAt(1)) },
        content: [{ type: 'text', text: (p.placeholder as string) || `[${type.toUpperCase()} Heading]` }],
      };

    case 'paragraph':
      return {
        type: 'paragraph',
        content: [{ type: 'text', text: (p.placeholder as string) || '[Paragraph content]' }],
      };

    case 'image':
      // NOTE: figure/figcaption not supported. Using just image node.
      return {
        type: 'image',
        attrs: {
          src: (p.src as string) || '',
          alt: (p.placeholder as string) || 'Image',
          title: (p.title as string) || undefined,
          caption: (p.caption as string) || undefined,
          isAIGenerated: Boolean(p.isAIGenerated),
        },
      };

    case 'list':
    case 'bullet-list':
    case 'numbered-list': {
      // Determine list type from explicit block type or listStyle prop
      const listStyle = p.listStyle as string;
      const isLettered = p.listType === 'lettered' || listStyle === 'lettered' || p.type === 'a';
      const isNumberedList = isLettered || block.type === 'numbered-list' || listStyle === 'numbered';
      const listType = isNumberedList ? 'orderedList' : 'bulletList';
      const items = (p.items as string[]) || ['Item 1', 'Item 2', 'Item 3'];
      return {
        type: listType,
        attrs: isLettered ? { type: 'a' } : undefined,
        content: items.map(item => ({
          type: 'listItem',
          content: [{ type: 'paragraph', content: [{ type: 'text', text: item }] }],
        })),
      };
    }

    case 'blockquote':
      return {
        type: 'blockquote',
        attrs: { attribution: p.attribution },
        content: [{ type: 'paragraph', content: [{ type: 'text', text: (p.placeholder as string) || '[Quote]' }] }],
      };

    case 'table': {
      // Build proper table structure - Table extension is installed (@tiptap/extension-table v3.13.0)
      const numCols = (p.columns as number) || 3;
      const numRows = (p.rows as number) || 3;
      const hasHeader = p.hasHeader !== false; // Default to true
      const headers = (p.headers as string[]) || [];
      const tableContent: RTENode[] = [];

      // Add header row if enabled
      if (hasHeader) {
        tableContent.push({
          type: 'tableRow',
          content: Array.from({ length: numCols }, (_, i) => ({
            type: 'tableHeader',
            content: [{ type: 'paragraph', content: [{ type: 'text', text: headers[i] || `Header ${i + 1}` }] }],
          })),
        });
      }

      // Add data rows
      for (let r = 0; r < numRows; r++) {
        tableContent.push({
          type: 'tableRow',
          content: Array.from({ length: numCols }, (_, c) => ({
            type: 'tableCell',
            content: [{ type: 'paragraph', content: [{ type: 'text', text: `Cell ${r + 1}-${c + 1}` }] }],
          })),
        });
      }

      return {
        type: 'table',
        attrs: { stylePreset: p.stylePreset || 'default' },
        content: tableContent,
      };
    }

    case 'key-takeaways':
      return {
        type: 'callout',
        attrs: {
          variant: 'key-takeaways',
          title: (p.title as string) || 'Key Takeaways',
          stylePreset: p.stylePreset || 'key-takeaways',
        },
        content: [
          {
            type: 'bulletList',
            content: ((p.items as string[]) || ['Point 1', 'Point 2', 'Point 3']).map(item => ({
              type: 'listItem',
              content: [{ type: 'paragraph', content: [{ type: 'text', text: item }] }],
            })),
          },
        ],
      };

    case 'pro-tip':
      return {
        type: 'callout',
        attrs: {
          variant: 'pro-tip',
          title: p.title as string | undefined,
          stylePreset: p.stylePreset || 'pro-tip',
        },
        content: [{ type: 'paragraph', content: [{ type: 'text', text: (p.placeholder as string) || '[Tip content]' }] }],
      };

    case 'warning':
      return {
        type: 'callout',
        attrs: {
          variant: 'warning',
          title: (p.title as string) || 'Warning',
          stylePreset: p.stylePreset || 'warning',
        },
        content: [{ type: 'paragraph', content: [{ type: 'text', text: (p.placeholder as string) || '[Warning content]' }] }],
      };

    case 'stats':
      return {
        type: 'statsGrid',
        attrs: { columns: (p.columns as number) || 3 },
        content: ((p.stats as Array<{ value: string; label: string }>) || [
          { value: '100+', label: 'Stat 1' },
          { value: '50%', label: 'Stat 2' },
          { value: '24/7', label: 'Stat 3' },
        ]).map(stat => ({
          type: 'statItem',
          attrs: { value: stat.value, label: stat.label },
        })),
      };

    case 'cta':
      return {
        type: 'ctaBlock',
        attrs: {
          buttonText: (p.buttonText as string) || '',
          buttonUrl: (p.url as string) || '#',
          variant: (p.style as string) || 'primary',
        },
      };

    case 'faq':
      return {
        type: 'faqSchema',
        content: ((p.questions as Array<{ question: string; answer: string }>) || [
          { question: 'Question 1?', answer: 'Answer 1' },
          { question: 'Question 2?', answer: 'Answer 2' },
        ]).map(item => ({
          type: 'faqItem',
          content: [
            {
              type: 'faqQuestion',
              content: [{ type: 'text', text: item.question }],
            },
            {
              type: 'faqAnswer',
              content: [{ type: 'paragraph', content: [{ type: 'text', text: item.answer }] }],
            },
          ],
        })),
      };

    case 'divider':
      return {
        type: 'horizontalRule',
        attrs: { style: `border-style: ${(p.style as string) || 'solid'}` },
      };

    default:
      return null;
  }
}

// =============================================================================
// Utility Functions
// =============================================================================

function escapeHTML(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function escapeAttr(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function safeExternalUrl(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return '';
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : '';
  } catch {
    return '';
  }
}

function stockSourceLabel(value: unknown): string {
  const source = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (source === 'pexels') return 'Pexels';
  if (source === 'unsplash') return 'Unsplash';
  return '';
}

function attributionLink(label: string, href: string): string {
  if (!href) return escapeHTML(label);
  // Stock-photo attribution URLs (Pexels/Unsplash) are genuinely external, but
  // classify defensively so an internal href never gets _blank + nofollow.
  const attrs = isInternalHref(href, activeSiteHost)
    ? ''
    : ' target="_blank" rel="nofollow noopener noreferrer"';
  return `<a href="${escapeAttr(href)}"${attrs}>${escapeHTML(label)}</a>`;
}

function renderImageCaptionHTML(attrs: Record<string, unknown> | undefined): string {
  if (!attrs) return '';

  const caption = typeof attrs.caption === 'string' ? attrs.caption.trim() : '';
  if (attrs.isAIGenerated && caption) {
    return escapeHTML(caption);
  }

  const attributionText = typeof attrs.attributionText === 'string' ? attrs.attributionText.trim() : '';
  const sourceLabel = stockSourceLabel(attrs.attributionSource || attrs.source);
  const photographerName = typeof attrs.photographerName === 'string' ? attrs.photographerName.trim() : '';
  const photographerUrl = safeExternalUrl(attrs.photographerUrl);
  const sourcePageUrl = safeExternalUrl(attrs.sourcePageUrl);
  const hasStockAttribution = Boolean(attrs.isStockPhoto || attributionText || photographerName);

  if (!hasStockAttribution) {
    return '';
  }

  if (photographerName && sourceLabel) {
    return `Photo by ${attributionLink(photographerName, photographerUrl)} on ${attributionLink(sourceLabel, sourcePageUrl)}`;
  }

  if (sourceLabel) {
    return `Photo via ${attributionLink(sourceLabel, sourcePageUrl)}`;
  }

  return attributionText ? escapeHTML(attributionText) : '';
}

// =============================================================================
// Validation Functions
// =============================================================================

/**
 * Known valid node types - MUST match TipTap registered extensions
 * StarterKit provides: doc, paragraph, heading, text, hardBreak, bulletList, orderedList, listItem, blockquote, codeBlock, horizontalRule
 * Table extensions provide: table, tableRow, tableCell, tableHeader
 * Custom extensions provide: callout, faqSchema, faqItem, faqQuestion, faqAnswer, statsGrid, statItem, ctaBlock
 * Image extension provides: image
 */
const VALID_NODE_TYPES = new Set([
  // StarterKit nodes
  'doc', 'paragraph', 'heading', 'text', 'hardBreak',
  'bulletList', 'orderedList', 'listItem',
  'blockquote', 'codeBlock', 'horizontalRule',
  // Image extension
  'image',
  // Table extensions
  'table', 'tableRow', 'tableCell', 'tableHeader',
  // Custom extensions
  'callout', 'statsGrid', 'statItem', 'ctaBlock',
  'faqSchema', 'faqItem', 'faqQuestion', 'faqAnswer',
  // Raw HTML (CTA injection - filtered from editor, preserved for publishing)
  'rawHtml',
]);

/**
 * Known valid mark types
 */
const VALID_MARK_TYPES = new Set(['bold', 'italic', 'underline', 'strike', 'code', 'link', 'textStyle']);

/**
 * Validate an RTE node structure
 * @param node - The node to validate
 * @param maxDepth - Maximum recursion depth for nested content validation
 * @returns Object with isValid boolean and optional errors array
 */
export function isValidRTENode(node: unknown, maxDepth: number = 50): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!node || typeof node !== 'object') {
    return { isValid: false, errors: ['Node must be a non-null object'] };
  }

  const n = node as Record<string, unknown>;

  // Check type
  if (typeof n.type !== 'string') {
    errors.push('Node must have a string "type" property');
  } else if (!VALID_NODE_TYPES.has(n.type)) {
    errors.push(`Unknown node type: "${n.type}"`);
  }

  // Text nodes must have text property
  if (n.type === 'text') {
    if (typeof n.text !== 'string') {
      errors.push('Text node must have a string "text" property');
    }
    // Validate marks if present
    if (n.marks !== undefined) {
      if (!Array.isArray(n.marks)) {
        errors.push('Text node "marks" must be an array');
      } else {
        for (const mark of n.marks) {
          if (!mark || typeof mark !== 'object' || typeof (mark as RTEMark).type !== 'string') {
            errors.push('Each mark must be an object with a string "type" property');
          } else if (!VALID_MARK_TYPES.has((mark as RTEMark).type)) {
            errors.push(`Unknown mark type: "${(mark as RTEMark).type}"`);
          }
        }
      }
    }
  }

  // Validate nested content
  if (n.content !== undefined) {
    if (!Array.isArray(n.content)) {
      errors.push('Node "content" must be an array');
    } else if (maxDepth > 0) {
      for (let i = 0; i < n.content.length; i++) {
        const childResult = isValidRTENode(n.content[i], maxDepth - 1);
        if (!childResult.isValid) {
          errors.push(`Invalid child at index ${i}: ${childResult.errors.join(', ')}`);
        }
      }
    }
  }

  // Validate attrs if present
  if (n.attrs !== undefined && (n.attrs === null || typeof n.attrs !== 'object')) {
    errors.push('Node "attrs" must be an object if present');
  }

  return { isValid: errors.length === 0, errors };
}

/**
 * Validate an RTE document structure
 * @param doc - The document to validate
 * @returns Object with isValid boolean and optional errors array
 */
export function validateRTEDocument(doc: unknown): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!doc || typeof doc !== 'object') {
    return { isValid: false, errors: ['Document must be a non-null object'] };
  }

  const d = doc as Record<string, unknown>;

  // Check type is 'doc'
  if (d.type !== 'doc') {
    errors.push(`Document type must be "doc", got "${d.type}"`);
  }

  // Content is optional but must be an array if present
  if (d.content !== undefined) {
    if (!Array.isArray(d.content)) {
      errors.push('Document "content" must be an array');
    } else {
      for (let i = 0; i < d.content.length; i++) {
        const nodeResult = isValidRTENode(d.content[i]);
        if (!nodeResult.isValid) {
          errors.push(`Invalid node at index ${i}: ${nodeResult.errors.join(', ')}`);
        }
      }
    }
  }

  // Meta is optional but must be an object if present
  if (d.meta !== undefined && (d.meta === null || typeof d.meta !== 'object')) {
    errors.push('Document "meta" must be an object if present');
  }

  return { isValid: errors.length === 0, errors };
}

/**
 * Normalize content arrays - return undefined instead of empty arrays
 * This is a cleanup utility to ensure consistent document structure
 */
export function normalizeRTEDocument(doc: RTEDocument): RTEDocument {
  return {
    ...doc,
    content: doc.content && doc.content.length > 0
      ? doc.content.map(normalizeRTENode).filter(Boolean) as RTENode[]
      : undefined,
  };
}

/**
 * Normalize a single RTE node - return undefined for empty content
 */
function normalizeRTENode(node: RTENode): RTENode {
  if (!node) return node;

  const normalizedContent = node.content && node.content.length > 0
    ? node.content.map(normalizeRTENode).filter(Boolean)
    : undefined;

  return {
    ...node,
    content: normalizedContent && normalizedContent.length > 0 ? normalizedContent : undefined,
    attrs: node.attrs && Object.keys(node.attrs).length > 0 ? node.attrs : undefined,
    marks: node.marks && node.marks.length > 0 ? node.marks : undefined,
  };
}

// =============================================================================
// Export types for external use
// =============================================================================

export type { TemplateBlock, BlockType, PreviewSpacing };
